import { useState, useEffect, useRef } from "react";
import PISNI_TEXTS from "./pisniTexts";
import BIBLE_TEXTS from "./bibleTexts";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const THEMES = {
  light: {
    pageBg: "#f0f4f8",
    headerBg: "#ffffff",
    headerBorder: "#d0dae8",
    headerShadow: "0 2px 8px rgba(30,58,95,0.08)",
    titleColor: "#1a3a6b",
    tabUnselectedBg: "#edf2f7",
    tabUnselectedText: "#718096",
    resetBorder: "#c0cfe0",
    resetColor: "#4a6080",
    itemText: "#1e2a3a",
    psalmText: "#8a6400",
    videoBadgeBg: "#1a3a6b",
    videoBadgeText: "#ffffff",
    topicText: "#2d7a2d",
    personText: "#64748b",
    refBorder: "#c0cfe0",
    refText: "#2563a8",
    refBody: "#3a6fa8",
    noteText: "#8a5000",
    noteBg: "rgba(184,110,0,0.09)",
    editBtnBg: "#edf2f7",
    editBtnBorder: "1px solid #c0cfe0",
    editBtnText: "#2563a8",
    deleteBtnBg: "#fff0f0",
    deleteBtnBorder: "1px solid #f0c0c0",
    deleteBtnText: "#c0392b",
    moveBtnBg: "#edf2f7",
    moveBtnBorder: "1px solid #c0cfe0",
    moveBtnText: "#4a6080",
    metaBarBg: "rgba(30,58,95,0.05)",
    metaBarBorder: "#d0dae8",
    metaValueColor: "#1a3a6b",
    addBtnBg: "#edf2f7",
    addBtnBorder: "2px dashed #c0cfe0",
    addBtnText: "#2563a8",
    modalOverlay: "rgba(0,0,0,0.45)",
    modalBg: "#ffffff",
    modalBorder: "#d0dae8",
    modalShadow: "0 8px 32px rgba(30,58,95,0.15)",
    modalTitle: "#1a3a6b",
    lblColor: "#4a6080",
    inpBg: "#f8fafc",
    inpBorder: "#c0cfe0",
    inpText: "#1e2a3a",
    checkboxText: "#64748b",
    saveBg: "#1a3a6b",
    saveText: "#ffffff",
    cancelBg: "#edf2f7",
    cancelText: "#64748b",
    viewBtnActiveBg: "#1a3a6b",
    viewBtnActiveText: "#ffffff",
    viewBtnBg: "#edf2f7",
    viewBtnText: "#1a3a6b",
    viewBtnBorder: "1px solid #c0cfe0",
    typeColors: {
      psalm:         { border: "#b8860b", bg: "rgba(184,134,11,0.08)",  num: "#7a5500", numText: "#ffffff" },
      scripture:     { border: "#2563a8", bg: "rgba(37,99,168,0.07)",   num: "#1a3a6b", numText: "#ffffff" },
      blessing:      { border: "#2563a8", bg: "rgba(37,99,168,0.07)",   num: "#1a3a6b", numText: "#ffffff" },
      offering_video:{ border: "#2563a8", bg: "rgba(37,99,168,0.07)",   num: "#1a3a6b", numText: "#ffffff" },
      sermon:        { border: "#2d7a2d", bg: "rgba(45,122,45,0.07)",   num: "#1a4a1a", numText: "#ffffff" },
      greetings:     { border: "#b86e00", bg: "rgba(184,110,0,0.07)",   num: "#7a4400", numText: "#ffffff" },
      music:         { border: "#c06090", bg: "rgba(192,96,144,0.07)",  num: "#7a2050", numText: "#ffffff" },
      default:       { border: "#8da8c8", bg: "rgba(30,58,95,0.06)",    num: "#3a6090", numText: "#ffffff" },
    },
  },
  dark: {
    pageBg: "#0d1117",
    headerBg: "#111827",
    headerBorder: "#1e3a5f",
    headerShadow: "none",
    titleColor: "#e2c97e",
    tabUnselectedBg: "#1c2333",
    tabUnselectedText: "#4a5568",
    resetBorder: "#1e3a5f",
    resetColor: "#6b7fa3",
    itemText: "#e2e8f0",
    psalmText: "#e2c97e",
    videoBadgeBg: "#1a3a6b",
    videoBadgeText: "#7ab3e0",
    topicText: "#7ed97e",
    personText: "#94a3b8",
    refBorder: "#1e3a5f",
    refText: "#7ab3e0",
    refBody: "#93b4dc",
    noteText: "#d4a017",
    noteBg: "rgba(212,160,23,0.08)",
    editBtnBg: "#1e3a5f",
    editBtnBorder: "none",
    editBtnText: "#7ab3e0",
    deleteBtnBg: "#2a1010",
    deleteBtnBorder: "none",
    deleteBtnText: "#e07070",
    moveBtnBg: "#1e3a5f",
    moveBtnBorder: "none",
    moveBtnText: "#7ab3e0",
    metaBarBg: "rgba(30,58,95,0.3)",
    metaBarBorder: "#1e3a5f",
    metaValueColor: "#e2c97e",
    addBtnBg: "transparent",
    addBtnBorder: "2px dashed #1e3a5f",
    addBtnText: "#7ab3e0",
    modalOverlay: "rgba(0,0,0,0.85)",
    modalBg: "#111827",
    modalBorder: "#1e3a5f",
    modalShadow: "none",
    modalTitle: "#e2c97e",
    lblColor: "#6b7fa3",
    inpBg: "#1c2333",
    inpBorder: "#1e3a5f",
    inpText: "#e2e8f0",
    checkboxText: "#94a3b8",
    saveBg: "#1a3a6b",
    saveText: "#e2c97e",
    cancelBg: "#1c2333",
    cancelText: "#94a3b8",
    viewBtnActiveBg: "#c9a84c",
    viewBtnActiveText: "#111",
    viewBtnBg: "#1e3a5f",
    viewBtnText: "#e2c97e",
    viewBtnBorder: "none",
    typeColors: {
      psalm:         { border: "#c9a84c", bg: "rgba(201,168,76,0.08)",  num: "#3a2c00", numText: "#e2c97e" },
      scripture:     { border: "#4a90d9", bg: "rgba(74,144,217,0.08)",  num: "#0e2040", numText: "#7ab3e0" },
      blessing:      { border: "#4a90d9", bg: "rgba(74,144,217,0.08)",  num: "#0e2040", numText: "#7ab3e0" },
      offering_video:{ border: "#4a90d9", bg: "rgba(74,144,217,0.08)",  num: "#0e2040", numText: "#7ab3e0" },
      sermon:        { border: "#7ed97e", bg: "rgba(126,217,126,0.07)", num: "#0e2a0e", numText: "#7ed97e" },
      greetings:     { border: "#d4a017", bg: "rgba(212,160,23,0.08)",  num: "#2a1f00", numText: "#d4a017" },
      music:         { border: "#e090c0", bg: "rgba(224,144,192,0.08)", num: "#5a1040", numText: "#e090c0" },
      default:       { border: "#1e3a5f", bg: "rgba(30,58,95,0.15)",    num: "#1e3a5f", numText: "#93b4dc" },
    },
  },
};

const TYPE_LABELS = {
  prayer:         "Молитва",
  psalm:          "Псалом",
  scripture:      "Читання з Біблії",
  sermon:         "Проповідь",
  blessing:       "Благословення",
  blessing_person:"Благословення особи",
  announcements:  "Об'яви",
  greetings:      "Привітання",
  offering_video: "Відео перед пожертвами",
  offering:           "Збір пожертв",
  music:              "Музичний номер",
  services_schedule:  "Розклад богослужінь",
};

const TYPE_DEFAULTS = {
  prayer:         { label: "Молитва" },
  psalm:          { label: "Загальний псалом", psalm: "", psalmName: "", video: false },
  scripture:      { label: "Закличне читання з Біблії", ref: "", text: "" },
  sermon:         { label: "Проповідь", topic: "" },
  blessing:       { label: "Тихі молитви і слова благословення", ref: "2 Кор. 13:13", text: "Благодать Господа нашого Ісуса Христа, і любов Бога й Отця, і причастя Святого Духа нехай буде зо всіма вами!" },
  blessing_person:{ label: "Благословення", person: "" },
  announcements:  { label: "Об'яви (новини громади)", note: "", note2: "", note3: "", note4: "" },
  greetings:      { label: "Передача привітів", note: "" },
  offering_video: { label: "Відео перед збором пожертв", ref: "", text: "" },
  offering:       { label: "Збір пожертв і святих десятин" },
  music:              { label: "Музичне виконання", performer: "" },
  services_schedule:  { label: "Богослужіння відбуваються:", friday: "", satMorning: "", satAfternoon: "" },
};

const TEMPLATES = {
  friday: {
    label: "П'ЯТНИЦЯ ВЕЧІР",
    color: "#1a3a6b",
    accent: "#ffffff",
    items: [
      { id: 1, type: "prayer",    label: "Тиха молитва" },
      { id: 2, type: "psalm",     label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 3, type: "scripture", label: "Вірш з Біблії", ref: "", text: "" },
      { id: 4, type: "prayer",    label: "Молитва" },
      { id: 5, type: "music",     label: "Музичне виконання", performer: "" },
      { id: 6, type: "sermon",    label: "Проповідь", topic: "" },
      { id: 9, type: "prayer",    label: "Молитва проповідуючого" },
      { id: 7, type: "psalm",     label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 8, type: "blessing",  label: "Тихі молитви і слова благословення", ref: "", text: "" },
    ]
  },
  satSchool: {
    label: "СУБОТНЯ ШКОЛА",
    color: "#7a3800",
    accent: "#ffffff",
    items: [
      { id: 1,  type: "prayer",        label: "Тиха молитва" },
      { id: 2,  type: "psalm",         label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 3,  type: "scripture",     label: "Вірш з Біблії", ref: "", text: "" },
      { id: 4,  type: "prayer",        label: "Молитва" },
      { id: 5,  type: "scripture",     label: "Місіонерські вісті", ref: "", text: "" },
      { id: 6,  type: "offering",      label: "Збір пожертв СШ" },
      { id: 7,  type: "prayer",        label: "Молитва (із за кафедри)" },
      { id: 8,  type: "music",         label: "Хор", performer: "" },
      { id: 9,  type: "announcements", label: "Об'ява", note: "Молодь і діти ідуть у свої класи. Прохання в 11.05 повернутися в зал для заключного псалму." },
      { id: 10, type: "sermon",        label: "Розбір СШ", topic: "" },
      { id: 11, type: "psalm",         label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 12, type: "prayer",        label: "Молитва ведучого" },
      { id: 13, type: "prayer",        label: "Перерва" },
    ]
  },
  satMorning: {
    label: "СУБОТА РАНОК",
    color: "#1a4a2e",
    accent: "#ffffff",
    items: [
      { id: 1, type: "prayer",         label: "Тиха молитва" },
      { id: 2, type: "psalm",          label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 3, type: "scripture",      label: "Вірш з Біблії", ref: "", text: "" },
      { id: 4, type: "prayer",         label: "Молитва" },
      { id: 5,  type: "services_schedule", label: "Богослужіння відбуваються:", friday: "", satMorning: "", satAfternoon: "" },
      { id: 6,  type: "announcements",  label: "Об'яви (новини громади)", note: "", note2: "", note3: "", note4: "" },
      { id: 7,  type: "greetings",      label: "Передача привітів", note: "Тепер хто має привіти, можете передати" },
      { id: 8,  type: "offering_video", label: "Читання перед збором", ref: "", text: "" },
      { id: 9,  type: "offering",       label: "Збір пожертв і святих десятин" },
      { id: 10, type: "prayer",         label: "Молитва за пожертви" },
      { id: 11, type: "music",          label: "Музичне виконання", performer: "" },
      { id: 12, type: "sermon",         label: "Проповідь", topic: "" },
      { id: 15, type: "prayer",         label: "Молитва проповідуючого" },
      { id: 13, type: "psalm",          label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 14, type: "blessing",       label: "Тихі молитви і слова благословення", ref: "", text: "" },
    ]
  },
  satAfternoon: {
    label: "СУБОТА ПІСЛЯ ОБІДУ",
    color: "#3a1a5f",
    accent: "#ffffff",
    items: [
      { id: 1, type: "prayer",    label: "Тиха молитва" },
      { id: 2, type: "psalm",     label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 3, type: "scripture", label: "Вірш з Біблії", ref: "", text: "" },
      { id: 4, type: "prayer",    label: "Молитва" },
      { id: 5, type: "music",     label: "Музичне виконання", performer: "" },
      { id: 6, type: "sermon",    label: "Проповідь", topic: "" },
      { id: 9, type: "prayer",    label: "Молитва проповідуючого" },
      { id: 7, type: "psalm",     label: "Загальний псалом", psalm: "", psalmName: "", video: false },
      { id: 8, type: "blessing",  label: "Тихі молитви і слова благословення", ref: "", text: "" },
    ]
  },
  communion: {
    label: "ВЕЧЕРЯ ГОСПОДНЯ",
    color: "#6b1a1a",
    accent: "#ffffff",
    items: [
      { id: 1,  type: "prayer",            label: "Тиха молитва" },
      { id: 2,  type: "psalm",             label: "Загальний псалом", psalm: "51", psalmName: "Славний наш", video: false, suggestions: ["51","33","104","57"] },
      { id: 3,  type: "scripture",         label: "Вірш з Біблії", ref: "", text: "" },
      { id: 4,  type: "prayer",            label: "Молитва" },
      { id: 5,  type: "services_schedule", label: "Богослужіння відбуваються:", friday: "", satMorning: "", satAfternoon: "" },
      { id: 6,  type: "announcements",     label: "Об'яви (новини громади)", note: "", note2: "", note3: "", note4: "" },
      { id: 7,  type: "greetings",         label: "Передача привітів", note: "Тепер хто має привіти, можете передати" },
      { id: 8,  type: "offering_video",    label: "Читання перед збором", ref: "", text: "" },
      { id: 9,  type: "offering",          label: "Збір пожертв і святих десятин" },
      { id: 10, type: "prayer",            label: "Молитва за пожертви" },
      { id: 11, type: "music",             label: "Музичне виконання", performer: "" },
      { id: 12, type: "sermon",            label: "Проповідь", topic: "" },
      { id: 13, type: "scripture",         label: "Слово перед служінням смирення", ref: "Іван 13:1-17", text: "" },
      { id: 14, type: "psalm",             label: "Загальний спів", psalm: "248", psalmName: "Господь свої веління", video: false },
      { id: 15, type: "announcements",     label: "Оголошення ходу служіння смирення (диякон)", note: "", note2: "", note3: "", note4: "" },
      { id: 16, type: "prayer",            label: "Молитва" },
      { id: 17, type: "prayer",            label: "Служіння смирення (ногоомиття)" },
      { id: 18, type: "prayer",            label: "Прийняття символів" },
      { id: 19, type: "psalm",             label: "Ламання Хліба", psalm: "251", psalmName: "Страждання час вже наступав (куп. 1,2)", video: false },
      { id: 20, type: "psalm",             label: "Вино", psalm: "251", psalmName: "Страждання час вже наступав (куп. 2,4)", video: false },
      { id: 21, type: "scripture",         label: "Обітниця надії", ref: "Мат. 26:29", text: "" },
      { id: 22, type: "psalm",             label: "Загальний псалом", psalm: "111", psalmName: "Господь мій Вождь", video: false, suggestions: ["111","7","237"] },
      { id: 23, type: "blessing",          label: "Тихі молитви і слова благословення", ref: "Чис. 6:24-26", text: "" },
    ]
  }
};

const PISNI_NADIYI = [
  {num:1,name:"БОЖЕ, СЛАВИМО ТЕБЕ"},{num:2,name:"ПЕРЕД ЄГОВИ СВІТЛИЙ ТРОН"},{num:3,name:"НАШ ГОСПОДЬ МІЖ НАМИ"},
  {num:4,name:"ЯК СЛАВЕН НАШ ГОСПОДЬ!"},{num:5,name:"БОГ Є ЛЮБОВ"},{num:6,name:"ПРОСЛАВМО БОГА СИЛ"},
  {num:7,name:"ХАЙ БУДЕ ТОБІ"},{num:8,name:"ЧУЮ, ЯК ЗНОВ ЛУНАЮТЬ В ПІСНІ"},{num:9,name:"БОГ Є ЛЮБОВ"},
  {num:10,name:"ЗЕМЛЯ ТРЕМТИТЬ"},{num:11,name:"БОГ Є ЛЮБОВ, ЛИШ ТОЙ, ХТО БОГА ЗНАЄ"},{num:12,name:"БОГ Є ЛЮБОВ — ВІН ЛЮДЕЙ СПАСАЄ"},
  {num:13,name:"ЯКБИ ЗНАВ Я"},{num:14,name:"ЧИТАЙ ПОСТІЙНО БОЖЕ СЛОВО"},{num:15,name:"В СЛОВІ БОЖІМ УТІХУ ШУКАЙ"},
  {num:16,name:"ЛЮБЛЮ ЧИТАТИ КНИГУ"},{num:17,name:"В ЧАС НІЧНИЙ"},{num:18,name:"ТВОЇ СЛОВА, МОВ ТА РОСА"},
  {num:19,name:"ЧЕРЕЗ ВСІ ТИСЯЧОЛІТТЯ"},{num:20,name:"НОВА РАДІСТЬ"},{num:21,name:"ТИХАЯ НІЧ"},
  {num:22,name:"ВЕЛИКА ЗОРЯ"},{num:23,name:"ХВАЛУ НЕСЕМ ХРИСТУ ПОВІК"},{num:24,name:"З ВИСОТ НЕБЕСНИХ"},
  {num:25,name:"ЧИ ТИ ЗНАЄШ РІЧЕНЬКУ ТУ?"},{num:26,name:"О ІСУСЕ НАЙСВЯТІШИЙ"},{num:27,name:"ЧИ ЗНАЄШ ПОДВИГ ІСУСА?"},
  {num:28,name:"ТАМ, ДЕ ІСУС РОЗП'ЯТИМ БУВ"},{num:29,name:"ЗАХОТІВ НАШ СПАСИТЕЛЬ"},{num:30,name:"НА ГОЛГОФУ ЙДИ, ДУШЕ"},
  {num:31,name:"ПОГЛЯНЬ НА ГОЛГОФУ"},{num:32,name:"БУЛО ТИХО В ГЕФСИМАНЬЇ"},{num:33,name:"САД ГЕФСИМАНСЬКИЙ"},
  {num:34,name:"О, ІСУСЕ НАЙСВЯТІШИЙ"},{num:35,name:"СИНА БОЖОГО В ГРОБІ НЕМА"},{num:36,name:"В МОГИЛІ ВІН ЛЕЖАВ"},
  {num:37,name:"ХРИСТОС ВОСКРЕС!"},{num:38,name:"СИНА БОЖОГО В ГРОБІ НЕМА"},{num:39,name:"РАДІСТЬ ЩЕДРО"},
  {num:40,name:"ШЛЯХ ДО СПАСІННЯ"},{num:41,name:"СЛУХАЙТЕ ВІСТКУ"},{num:42,name:"БОГ СИНА В СВІТ З НЕБЕС ПОСЛАВ"},
  {num:43,name:"ГОСПОДИ, СКАЛО МОЯ"},{num:44,name:"ВЕЛИКИЙ ЛІКАР І ТВОРЕЦЬ"},{num:45,name:"ХРИСТОВА КРОВ"},
  {num:46,name:"ЩО ЗМИВАЄ ВСЯКИЙ ГРІХ"},{num:47,name:"ЯК СУМНО БУВАЄ МЕНІ"},{num:48,name:"О, ГАЛИЛЕЯ!"},
  {num:49,name:"НАЙКРАЩЕ ІМ'Я Є НА СВІТІ"},{num:50,name:"ЯСНИЙ ЦЕЙ ШЛЯХ"},{num:51,name:"СЛАВНИЙ НАШ ХРИСТОС"},
  {num:52,name:"ІСУС У СВІТ ПРИЙШОВ"},{num:53,name:"ЛЮБИТЬ НАС ХРИСТОС"},{num:54,name:"ЗА ВСЕ ТОБІ Я ДЯКУЮ"},
  {num:55,name:"НЕ ЗНАЮЧИ БОГА"},{num:56,name:"ХВАЛІТЬ ІСУСА, БОГА СИЛ"},{num:57,name:"ТВОРЦЮ СПІВАЮ ГІМН ХВАЛИ"},
  {num:58,name:"ХВАЛА ХРИСТУ!"},{num:59,name:"ГОСПОДЬ МОЄ СВІТЛО"},{num:60,name:"Я СПІВАЮ, ЯК СПАСИТЕЛЬ"},
  {num:61,name:"БУДУ СПІВАТИ"},{num:62,name:"РАДІЙТЕ, СПІВАЙТЕ І СЛАВТЕ"},{num:63,name:"КОЛИ ПОЧУЮ ПРО ХРИСТА"},
  {num:64,name:"Я ХОЧУ СПІВАТИ"},{num:65,name:"БРАТЕ ЗНОВУ НАГАДАЙ"},{num:66,name:"ПРИЙДИ З ВИСОТ"},
  {num:67,name:"ДУХУ ПРАВДИ І ЛЮБОВІ"},{num:68,name:"ДУХ СВЯТИЙ, ПРИЙДИ"},{num:69,name:"ДУХ СВЯТИЙ ЗАКЛИКАЄ"},
  {num:70,name:"ВСЕВИШНІЙ БОГ НАШ"},{num:71,name:"ІСУСЕ МІЙ, ДО ТЕБЕ ЙДУ"},{num:72,name:"В ДОЛИНІ ГРІХА"},
  {num:73,name:"ПРОСІТЕ І ДАНО ВАМ БУДЕ"},{num:74,name:"ПРОСЛАВЛЯЙТЕ БОГА"},{num:75,name:"ОСЬ ЗІБРАЛИСЬ ЗНОВУ"},
  {num:76,name:"ТАКИЙ, ЯК Є, СЬОГОДНІ"},{num:77,name:"ЯК ГАРНО НА НЕБІ"},{num:78,name:"БОЖЕ МІЙ, ДЖЕРЕЛО ЩАСТЯ"},
  {num:79,name:"БОЖЕ НАШ СВЯТИЙ, ЄГОВО!"},{num:80,name:"ТВОРЕЦЬ МІЙ І ГОСПОДЬ"},{num:81,name:"О ІСУСЕ ЧЕРЕЗ ХВИЛІ"},
  {num:82,name:"ВІЗЬМИ МЕНЕ ЗА РУКИ"},{num:83,name:"ІСУСЕ МІЙ ПРИЙДИ"},{num:84,name:"О, БОЖЕ, БУДЬ ТИ З НАМИ"},
  {num:85,name:"БОГУ СЛАВА І ХВАЛІННЯ"},{num:86,name:"БОГОСЛУЖІННЯ СКІНЧИЛОСЬ"},{num:87,name:"МІЙ ДОРОГИЙ ІСУСЕ"},
  {num:88,name:"ДЕНЬ ЦЕЙ СУБОТНІЙ!"},{num:89,name:"СУБОТА ДАНА ЛЮДЯМ БОГОМ"},{num:90,name:"СПОЧАТКУ, ЯК ГОСПОДЬ"},
  {num:91,name:"Я — ВІНЕЦЬ ТВОРІННЯ"},{num:92,name:"ДЕНЬ СУБОТНЬОГО СПОКОЮ"},{num:93,name:"ЗАМОВКНИ СУЄТО"},
  {num:94,name:"ЯК ЛЮБО І ЛЕГКО НА СЕРЦІ МЕНІ"},{num:95,name:"ПОГЛЯНЬТЕ, ЯК СЬОГОДНІ"},{num:96,name:"СУБОТНІЙ ДЕНЬ ПРИЙШОВ"},
  {num:97,name:"СУБОТНІЙ ДЕНЬ СВЯТОГО СПІЛКУВАННЯ!"},{num:98,name:"СЛАВНИЙ, СВЯТИЙ ДЕНЬ"},{num:99,name:"БОЖЕ, ГЛЯНЬ, ТВОЇ МИ ДІТИ"},
  {num:100,name:"АЛІЛУЯ, ДЕНЬ СУБОТНІЙ!"},{num:101,name:"ЯКИЙ ЩАСЛИВИЙ ДЕНЬ ДЛЯ НАС!"},{num:102,name:"ЗЕМНІ ТУРБОТИ ЗАЛИШИ"},
  {num:103,name:"УТІХУ НЕБЕСНУ"},{num:104,name:"ПРОСНУВШИСЬ В ЦЕЙ СУБОТНІЙ ДЕНЬ"},{num:105,name:"СУБОТНІЙ ДЕНЬ ПРИЙШОВ"},
  {num:106,name:"О, ЯКІ ВЕСЕЛІ МИ СЬОГОДНІ ВСІ"},{num:107,name:"З РАДІСНИМ СЕРЦЕМ"},{num:108,name:"БОЖЕ, ДО ТЕБЕ ЙДУ"},
  {num:109,name:"СПАСИТЕЛЮ МІЙ ЛЮБИЙ"},{num:110,name:"Я — НЕ СВІЙ"},{num:111,name:"ГОСПОДЬ — МІЙ ВОЖДЬ"},
  {num:112,name:"СЕРЦЕ МОЄ"},{num:113,name:"ПО ШЛЯХУ ЖИТТЯ"},{num:114,name:"ЗА ТОБОЮ, МІЙ ІСУСЕ"},
  {num:115,name:"ЩО ПОТРІБНО БІЛЬШ, ІСУСЕ?"},{num:116,name:"ІСУСЕ, ДОБРИЙ МІЙ"},{num:117,name:"В СЛАВНИЙ КРАЙ ЧУДЕС"},
  {num:118,name:"ЧУЮ ГОЛОС Я ІСУСА"},{num:119,name:"ІСУСЕ МІЙ, Я ХОЧУ ТЕБЕ ЗНАТИ"},{num:120,name:"БУВ ТОЙ ЧАС"},
  {num:121,name:"В НАШИХ РУКАХ ПРАПОР БОГА ЖИВОГО"},{num:122,name:"ВПЕРЕД ВСІ ЗА ІСУСОМ"},{num:123,name:"ЯК СВІТЛО ДНЯ"},
  {num:124,name:"СВІЙ МАЯК"},{num:125,name:"БОЖА ПРАВДА СВІТИТЬ ЯСНО"},{num:126,name:"СПОКІЙ НЕБЕСНИЙ"},
  {num:127,name:"О БОЖЕ МІЙ"},{num:128,name:"ХТО З ВІРОЮ ГЛЯНЕ"},{num:129,name:"ХОЧ ТВІЙ ХРЕСТ ВАЖКИЙ ПОРОЮ"},
  {num:130,name:"ТВЕРДО Я ВІРЮ"},{num:131,name:"ВІД ГРІХА ВІЛЬНИЙ"},{num:132,name:"МИ ІДЕМО В ХАНААН"},
  {num:133,name:"ЗА СВЯТУ ХРИСТОВУ ВІРУ"},{num:134,name:"СИЛЬНУ ВІРУ, БРАТЕ МАЙ"},{num:135,name:"КОЛИ БОЖИЙ МИР НАПОВНЯЄ СЕРЦЯ"},
  {num:136,name:"ВІР, ДУШЕ, НАДІЙСЯ!"},{num:137,name:"КОЛИ МІЙ ДУХ ОСЛАБНЕ ТУТ ПОРОЮ"},{num:138,name:"БРАТТЯ, ВСІ РАДІЙТЕ!"},
  {num:139,name:"ПОЧУВ Я ПРО ЦЕРКВУ"},{num:140,name:"РАДІСТЬ НЕВИМОВНУ МАЮТЬ НЕБЕСА"},{num:141,name:"РАДІСНО ПІСНЯ ЛУНАЄ"},
  {num:142,name:"РАДІСТЬ ЩИРА І ХВАЛІННЯ"},{num:143,name:"ЯКЩО ВАЖКО НАМ"},{num:144,name:"ХОЧ І ВАЖКО НЕРАЗ"},
  {num:145,name:"ЯКЩО ТИ ЗНЕМОЖЕНИЙ"},{num:146,name:"УКРИЙ СВОЄ ГОРЕ"},{num:147,name:"ДОРОГІ СЛОВА ІСУСА"},
  {num:148,name:"ДРУЖЕ, З ГРІХОМ ТИ БОРИСЯ"},{num:149,name:"З НАМИ БОГ!"},{num:150,name:"ВОЇНІВ СВОЇХ ГОСПОДЬ ПРОВАДИТЬ В БІЙ"},
  {num:151,name:"ЯКЩО ТЕБЕ ТЯГАР ГНІТИТЬ"},{num:152,name:"МИР — ЦЕ Є МРІЯ"},{num:153,name:"НА СЕРЦІ ЛИШ ПІСНЯ ОДНА"},
  {num:154,name:"ІСУСА ЛЮБЛЮ Я"},{num:155,name:"ХОЧ БИ Я ВСІ ЗЕМНІ СКАРБИ"},{num:156,name:"ЗА СВЯТУ ХРИСТОВУ ВІРУ"},
  {num:157,name:"О, ІСУСЕ, ДАЙ СИЛИ МЕНІ"},{num:158,name:"СЛУГИ ХРИСТОВІ"},{num:159,name:"ІДІТЬ, ПОСЛИ"},
  {num:160,name:"СЛУХАЙТЕ, ЯК БОЖИЙ ГОЛОС"},{num:161,name:"О, ДАЙ НАМ, БОЖЕ"},{num:162,name:"ОДНЕ ЛИШ ЛИСТЯ"},
  {num:163,name:"ДЕХТО ЗВЕРШИВ ТРУД"},{num:164,name:"БРАТТЯ, СКОРІШ ДО ДІЛА"},{num:165,name:"ВСІ ДО ПРАЦІ СПІШІТЬ"},
  {num:166,name:"ІДИ В ВИНОГРАДНИК"},{num:167,name:"О, ДЕ Ж ТІ ЖЕНЦІ"},{num:168,name:"ХТО ДОБРО ТУТ СІЄ"},
  {num:169,name:"ВСЮДИ ТАК БАГАТО ПРАЦІ"},{num:170,name:"ДРУЖНО В ЗГОДІ ДЛЯ ХРИСТА ПРАЦЮЄМ"},{num:171,name:"БОЖА ЛЮБОВ ОБНІМАЄ"},
  {num:172,name:"О, ДРУЖЕ, РОЗКАЖИ"},{num:173,name:"ОВЕЦЬ ЗАБЛУДНИХ ПАСТИР ШУКАЄ"},{num:174,name:"В ЦАРСТВО БОЖЕ ТИМ ДОРОГА"},
  {num:175,name:"О, ІСУСЕ ЛЮБИЙ"},{num:176,name:"О, СМЕРТНИЙ, ВВАЖАЙ!"},{num:177,name:"НАД ЗЕМЛЕЮ І МОРЯМИ"},
  {num:178,name:"ЧУЄШ ВІСТКУ, ЩО ЛУНАЄ?"},{num:179,name:"ПО ВСІХ КРАЯХ ЗЕМЛІ"},{num:180,name:"ДВА ШЛЯХИ"},
  {num:181,name:"ЛАГІДНО Й НІЖНО СЬОГОДНІ БОГ КЛИЧЕ"},{num:182,name:"Я ЗНАЮ СТРУМОК"},{num:183,name:"В КРАЇНІ ДАЛЕКІЙ"},
  {num:184,name:"ХОЧЕ ВСІХ ЛЮДЕЙ ГОСПОДЬ БЛАГОСЛОВИТЬ"},{num:185,name:"ВЕРНИСЬ, ВЕРНИСЬ!"},{num:186,name:"СЛІПИЙ, НЕЩАСНИЙ ВАРТИМЕЙ"},
  {num:187,name:"О, СЛОВО БОЖЕ ВІЧНЕ"},{num:188,name:"ПРИЙДИ ТИ ДО ХРИСТА"},{num:189,name:"Я ЧУЮ НІЖНИЙ КЛИЧ ХРИСТА"},
  {num:190,name:"ПРИЙДИ ТИ ДО ІСУСА"},{num:191,name:"ДО ХРИСТА ВСІХ ПРИКЛИКАЙТЕ"},{num:192,name:"ПРИЙМЕ ГРІШНИКІВ ХРИСТОС"},
  {num:193,name:"СЛУХАЙ, МІЙ ДРУЖЕ"},{num:194,name:"ЧУЄШ ГОЛОС ТИ ІСУСА?"},{num:195,name:"СПАСІННЯ БОГ ПОДАРУВАВ"},
  {num:196,name:"ЧИ НЕМА ВЖЕ В ТЕБЕ МІСЦЯ?"},{num:197,name:"Я СТОЮ БІЛЯ ДВЕРЕЙ"},{num:198,name:"ПРОБУДИСЬ ВІД СНУ, МІЙ ДРУЖЕ"},
  {num:199,name:"САМАРЯНКА"},{num:200,name:"ЧУТИ СТУКІТ ЛАГІДНИЙ"},{num:201,name:"ЩО Я ДАМ ТОБІ?"},
  {num:202,name:"СПАСИТЕЛЯ МАЮ Я В СЛАВІ НЕБЕСНІЙ"},{num:203,name:"ПРИЙДІТЬ, Я РОЗКАЖУ ВАМ"},{num:204,name:"Я БУВ НЕПОКІРНИМ СИНОМ"},
  {num:205,name:"ХРИСТОС ВИЙШОВ З ХРАМУ"},{num:206,name:"СТОРОЖ, СКАЖИ!"},{num:207,name:"СПАСИТЕЛЬ ПРИЙДЕ БЕЗ ВАГАННЯ"},
  {num:208,name:"ПИЛЬНО ГЛЯДІТЬ ЗА ЗНАКАМИ УСІ"},{num:209,name:"В ТОЙ ДЕНЬ"},{num:210,name:"СВЯТІ ОБІТНИЦІ ХРИСТА"},
  {num:211,name:"ЯК ШВИДКО ДЕНЬ ЗА ДНЕМ ЛЕТИТЬ"},{num:212,name:"СКОРО ПРИЙДЕ СПАСИТЕЛЬ"},{num:213,name:"ГОСПОДЬ МІЙ ПРИЙДЕ!"},
  {num:214,name:"Я ЧУЮ, ЯК СВІТ ЦЕЙ, ЯК МОРЕ, ШУМИТЬ"},{num:215,name:"ОСЬ ПРИЙДЕ ХРИСТОС"},{num:216,name:"ІСУСЕ, ОСЬ ТВОЯ СІМ'Я"},
  {num:217,name:"ШВИДКО ТАК ЖИТТЯ МИНАЄ"},{num:218,name:"ЯК ПРИЙДЕ ГОСПОДЬ"},{num:219,name:"В ДЕНЬ, КОЛИ ТРУБА ГОСПОДНЯ"},
  {num:220,name:"БИЙТЕ ТРИВОГУ"},{num:221,name:"В РІДНИЙ КРАЙ"},{num:222,name:"ХТО ТІ, ЩО З ВІТТЯМ?"},
  {num:223,name:"НАД КРИСТАЛЬНОЮ РІКОЮ"},{num:224,name:"О, ЯК РАДІСНО НАМ ЧУТИ"},{num:225,name:"ВЖЕ БАЧУ РІДНИЙ ХАНААН"},
  {num:226,name:"МИ ІДЕМО В КРАЙ НЕБЕСНИЙ"},{num:227,name:"ВІТЧИЗНА МОЯ В НЕБЕСАХ"},{num:228,name:"Я — МАНДРІВНИК ЗЕМНИЙ"},
  {num:229,name:"ОНОВЛЕНЕ НЕБО І ЗЕМЛЮ"},{num:230,name:"В КРАЙ НЕБЕСНИЙ"},{num:231,name:"МИ ТУТ ЛИШ ГОСТІ"},
  {num:232,name:"ІДУТЬ ПОДОРОЖНІ"},{num:233,name:"МИ ПО ВОДАХ ОКЕАНУ"},{num:234,name:"ВЖЕ НАБЛИЖАЄТЬСЯ ТОЙ ЧАС"},
  {num:235,name:"КРАЇНА В НЕБІ Є СВЯТА"},{num:236,name:"ЗАСПІВАЙМО ПІСНЮ"},{num:237,name:"ХТО ПІДЕ З НАМИ?"},
  {num:238,name:"АХ, РАДІСТЬ, РАДІСТЬ БУДЕ ТАМ"},{num:239,name:"ПРЕКРАСНИЙ БІЛИЙ СТРІЙ"},{num:240,name:"З БОГОМ МИ СМІЛО!"},
  {num:241,name:"СЛОВО ПРАВДИ ВІЧНЕ"},{num:242,name:"О, НІ, НІХТО НАС НЕ РОЗЛУЧИТЬ!"},{num:243,name:"ЩАСЛИВИЙ ДЕНЬ!"},
  {num:244,name:"ЯК КОЛИСЬ ПЕРЕД ЙОРДАНОМ"},{num:245,name:"ГОСПОДЬ НАВЧАВ УЧЕНИКІВ"},{num:246,name:"Я З ХРИСТОМ В ЗАВІТ ВСТУПАЮ"},
  {num:247,name:"БОЖЕ, НА ТВОЇЙ ВЕЧЕРІ"},{num:248,name:"ГОСПОДЬ СВОЇ ВЕЛІННЯ"},{num:249,name:"ХТО З ВАС, БРАТТЯ ДОРОГІ?"},
  {num:250,name:"ПРИЙДІТЬ ВІДКУПЛЕНІ"},{num:251,name:"СТРАЖДАННЯ ЧАС ВЖЕ НАСТУПАВ"},{num:252,name:"СТРАШНО БУШУЄ ЖИТТЄВЕЄ МОРЕ"},
  {num:253,name:"ПРОСТИ МЕНЕ, ІСУСЕ"},{num:254,name:"ОБІЗВИСЬ, МІЙ МИЛИЙ БОЖЕ"},{num:255,name:"НАУЧИ МЕНЕ, БОЖЕ, МОЛИТИСЬ"},
  {num:256,name:"БОЖЕ, В ЦЕЙ МОЛИТВИ ЧАС"},{num:257,name:"ЯКЩО ТВІЙ ДУХ ВЖЕ ВТОМИВСЯ"},{num:258,name:"О БОЖЕ, ДАЙ СИЛИ"},
  {num:259,name:"КОЛИ В ЖИТТІ ЦІМ Я ВТОМЛЮСЯ"},{num:260,name:"НЕ МИНАЙ МЕНЕ, ІСУСЕ"},{num:261,name:"ТИ ЗНАЄШ, БОЖЕ"},
  {num:262,name:"Я УТОМЛЕНИЙ, БЕЗСИЛИЙ"},{num:263,name:"ПОЧУЙ ЖЕ, ГОСПОДИ, МОЛІННЯ"},{num:264,name:"О БОЖЕ, БОЖЕ, ДАЙ ЖЕ СИЛИ"},
  {num:265,name:"БОЖЕ, ОТЧЕ ВСЕМОГУТНІЙ"},{num:266,name:"БУДЬТЕ ПИЛЬНІ"},{num:267,name:"ДАЙ СВЯТІСТЬ НАМ, БОЖЕ"},
  {num:268,name:"ПРИ БОЖІМ ТРОНІ В НЕБЕСАХ"},{num:269,name:"КНИГА ДАВНЯ І СВЯТА"},{num:270,name:"РАДІСНІ ТІ ДІТИ"},
  {num:271,name:"ПТАШОК МАЛЕНЬКИХ ЛЮБИТЬ БОГ"},{num:272,name:"ГЛЯНЬТЕ, ЩО ЗА РАНОК"},{num:273,name:"ГЛЯНЬ ВГОРУ!"},
  {num:274,name:"НАСТРОЙ СТРУНИ"},{num:275,name:"КРАЩІ ЛІТА МОЛОДІЇ"},{num:276,name:"ТОБІ Я СЛУЖИТИ БАЖАЮ"},
  {num:277,name:"ХВАЛУ, ГОСПОДЬ, ТОБІ ПРИНОСИМ"},{num:278,name:"ГОСПОДЬ МІЖ НАМИ ДУХОМ САМ"},{num:279,name:"ЗА ВСЕ ТОБІ Я ДЯКУЮ"},
  {num:280,name:"О, БОЖЕ, БЛАГАЄМ"},{num:281,name:"ДЕ БОЖИЙ ДУХ В СЕРЦЯХ ЖИВЕ"},{num:282,name:"БОЖЕ, З НЕБА ТИ ЗІЙДИ"},
  {num:283,name:"ОСЬ ПЕРЕД НАМИ СТОЯТЬ МОЛОДІ"},{num:284,name:"О ГОСПОДИ У ГАЛІЛЕЮ"},{num:285,name:"СПИ ТИХИМ І СПОКІЙНИМ СНОМ"},
  {num:286,name:"ЗВЕЛИЧУЙ БОГА"},{num:287,name:"НЕСЕМ МИ ТІЛО НА СПОКІЙ"},{num:288,name:"СХИЛИЛОСЬ СОНЦЕ"},
  {num:289,name:"СПОКІЙНО СПИ!"},{num:290,name:"ВІРНИХ БОГУ"},{num:291,name:"МИ ПОБАЧИМОСЬ НАПЕВНО"},
  {num:292,name:"ВПЕРЕД! ПІДЕМ МИ В НОВИЙ РІК"},{num:293,name:"ЗАБУДЕМ ГОРЕ І ТОМЛІННЯ"},{num:294,name:"БОГ ДОБРИМ БУВ!"},
  {num:295,name:"О, БОЖЕ, МИ — ТВОЯ СІМ'Я"},{num:296,name:"ЛЮБЛЮ ГОСПОДНІЙ ДІМ"},{num:297,name:"ДОРОГІ НАМ ХВИЛИНИ ГОСПОДЬ ДАРУВАВ"},
  {num:298,name:"БОГ З ТОБОЮ — МИ ЗУСТРІНЕМОСЬ!"},{num:299,name:"ІСУСЕ, ТВІЙ БЛАЖЕННИЙ ТРУД"},{num:300,name:"ЗНОВУ ОСІНЬ НАСТУПИЛА"},
  {num:301,name:"ДО НАС ПРИЙШЛА НОВА ВЕСНА"},{num:302,name:"ХВАЛІТЕ ГОСПОДА"},{num:303,name:"О БОЖЕ, ЯК ЗБАГНУТИ"},
  {num:304,name:"СЬОГОДНІ ДЕНЬ СВЯТИЙ"},{num:305,name:"ДУШЕ МОЯ"},{num:306,name:"ДРІМАЄ ГЕФСИМАНСЬКИЙ САД"},
  {num:307,name:"БЛАГОДАТЬ ГОСПОДА"},{num:308,name:"ЗАБУДЬ ТУРБОТИ"},{num:309,name:"МИ СЛАВИМ ТЕБЕ"},
  {num:310,name:"ВПЕРЕД, В ІМ'Я ГОСПОДНЄ"},{num:311,name:"ВІРИМО МИ"},{num:312,name:"ЛУНАЄ НАД СВІТОМ"},
  {num:313,name:"Я СТУКАЮ В ДВЕРІ"},{num:314,name:"ВСТАВАЙ, ДОЧКО СІОНУ!"},{num:315,name:"О, ЛЮДИ, ПОКАЙТЕСЬ!"},
  {num:316,name:"СЛУХАЙ, ЯК ГОСПОДЬ ВЕЛИТЬ"},{num:317,name:"МОВ ТА ПТАШКА"},{num:318,name:"ЯКБИ КРИЛА Я МАВ"},
  {num:319,name:"ХРИСТОС ЧУДОВЕ МІСТО НАМ"},{num:320,name:"ОТЧЕ НАШ"},{num:321,name:"ОТЧЕ, ПОЧУЙ НАС"},
  {num:322,name:"БОЖЕ, ПОГЛЯНЬ"},{num:323,name:"ХРИСТОС, ПОЧУЙ"},{num:324,name:"У КАНІ — ВЕСІЛЛЯ"},
  {num:325,name:"ПРИЙМИ, ТВОРЕЦЬ, ХВАЛУ"},{num:326,name:"О, ТИ, КОГО ХВАЛИТЬ"},
];

const UA_MONTHS = ['січ','лют','бер','квіт','трав','черв','лип','серп','вер','жовт','лист','груд'];
const UA_MONTHS_FULL = ['січня','лютого','березня','квітня','травня','червня','липня','серпня','вересня','жовтня','листопада','грудня'];

function formatUsedDate(dateStr) {
  const d = new Date(dateStr);
  return `${d.getDate()} ${UA_MONTHS[d.getMonth()]}`;
}

// YYYY-MM-DD → "31 травня 2026"; для старих текстових значень повертає як є
function formatDateUkr(val) {
  if (!val) return "";
  const m = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${parseInt(m[3],10)} ${UA_MONTHS_FULL[parseInt(m[2],10)-1]} ${m[1]}`;
  return val;
}

function isWithin2Months(dateStr) {
  if (!dateStr) return false;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff < 60 * 24 * 60 * 60 * 1000;
}

function SongPickerModal({ onSelect, onClose, th, history }) {
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const inp = { width:"100%", background:th.inpBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, padding:"10px 14px", color:th.inpText, fontSize:15, outline:"none", boxSizing:"border-box" };
  const filtered = search
    ? PISNI_NADIYI.filter(s => String(s.num).startsWith(search.trim()) || s.name.toLowerCase().includes(search.toLowerCase()))
    : PISNI_NADIYI;

  if (preview) {
    const text = PISNI_TEXTS[preview.num];
    return (
      <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}>
        <div style={{ background:th.modalBg, borderRadius:16, padding:20, width:"100%", maxWidth:560, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"90vh", display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ fontSize:13, color:th.refText, fontWeight:700 }}>№{preview.num}</div>
          <div style={{ fontSize:17, fontWeight:800, color:th.modalTitle, lineHeight:1.3 }}>{preview.name}</div>
          <div style={{ overflowY:"auto", flex:1, borderTop:`1px solid ${th.modalBorder}`, paddingTop:12 }}>
            {text
              ? <pre style={{ fontFamily:"Arial, sans-serif", fontSize:15, color:th.itemText, lineHeight:1.8, whiteSpace:"pre-wrap", margin:0 }}>{text}</pre>
              : <div style={{ color:th.lblColor, fontStyle:"italic" }}>Текст відсутній</div>
            }
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={()=>setPreview(null)} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"11px 0", flex:1, cursor:"pointer", fontSize:15 }}>← Назад</button>
            <button onClick={()=>onSelect(preview)} style={{ background:th.saveBg, color:th.saveText, border:"none", borderRadius:10, padding:"11px 0", flex:2, cursor:"pointer", fontSize:15, fontWeight:800 }}>Вибрати цю пісню</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}>
      <div style={{ background:th.modalBg, borderRadius:16, padding:20, width:"100%", maxWidth:500, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"85vh", display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ fontSize:16, fontWeight:800, color:th.modalTitle }}>«Пісні надії» — {PISNI_NADIYI.length} пісень</div>
        <input style={inp} placeholder="Пошук за номером або назвою..." value={search} onChange={e=>setSearch(e.target.value)} autoFocus/>
        <div style={{ overflowY:"auto", flex:1 }}>
          {filtered.map(s => {
            const usedDate = history[String(s.num)];
            const recent = isWithin2Months(usedDate);
            return (
              <div key={s.num} onClick={()=>setPreview(s)}
                style={{ display:"flex", gap:10, alignItems:"center", padding:"7px 10px", borderRadius:8, cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background=th.metaBarBg}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}
              >
                <span style={{ color:th.refText, fontWeight:700, minWidth:36, flexShrink:0, fontSize:13 }}>{s.num}.</span>
                <span style={{ color:th.itemText, fontSize:14, lineHeight:1.4, flex:1 }}>{s.name}</span>
                {recent && <span style={{ fontSize:11, color:"#b86e00", background:"rgba(184,110,0,0.12)", borderRadius:5, padding:"2px 7px", flexShrink:0, whiteSpace:"nowrap" }}>✓ {formatUsedDate(usedDate)}</span>}
              </div>
            );
          })}
        </div>
        <button onClick={onClose} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"10px 0", cursor:"pointer", fontSize:15 }}>Закрити</button>
      </div>
    </div>
  );
}

const BIBLE_REFS = [
  { book: "Псалми", refs: [
    "Пс. 3:5-6","Пс. 5:2-8","Пс. 5:12-13","Пс. 8:4-5","Пс. 9:2-3","Пс. 9:27-28",
    "Пс. 10:4-5","Пс. 15:7-9","Пс. 16:6-8","Пс. 17:32-34","Пс. 24:4-5","Пс. 27:1-2,6-7",
    "Пс. 29:5-6","Пс. 30:24-25","Пс. 31:5-8","Пс. 32:1-5","Пс. 33:2-6","Пс. 33:18-20",
    "Пс. 35:6-8","Пс. 36:3-6","Пс. 39:2-6","Пс. 39:17-18","Пс. 41:2-3","Пс. 46:7-9",
    "Пс. 47:10-11,15","Пс. 49:14-15,23","Пс. 50:17-19","Пс. 56:2-3","Пс. 56:10-12",
    "Пс. 61:6-9","Пс. 62:2-5","Пс. 65:16-20","Пс. 66:2-5","Пс. 68:31-35","Пс. 83:10-13",
    "Пс. 85:3-6","Пс. 90:1-4","Пс. 90:14-16","Пс. 91:2-5","Пс. 91:13-16","Пс. 94:1-7",
    "Пс. 95:1-3","Пс. 96:9-12","Пс. 97:4-9","Пс. 98:5-9","Пс. 99:1-4","Пс. 101:2-3",
    "Пс. 102:19-22","Пс. 103:33-34","Пс. 104:1-5","Пс. 107:4-6","Пс. 110:1-3","Пс. 112:1-6",
    "Пс. 113:23-26","Пс. 114:1-9","Пс. 117:14-17","Пс. 117:21,28-29","Пс. 118:10-12",
    "Пс. 118:33-37","Пс. 118:145-149","Пс. 118:169-173","Пс. 120:1-3","Пс. 121:1",
    "Пс. 129:2-5","Пс. 132:1-3","Пс. 134:1-3","Пс. 135:1-4","Пс. 137:1-6","Пс. 138:1-5",
    "Пс. 138:7-12","Пс. 139:12-14","Пс. 140:1-4","Пс. 141:5-7","Пс. 142:1-2","Пс. 142:5-8",
    "Пс. 144:1-3","Пс. 144:18-21","Пс. 146:5-11","Пс. 148:11-13","Пс. 149:1-4","Пс. 150:1-6",
  ]},
  { book: "Притчі", refs: [
    "Притч. 2:6-10","Притч. 3:5-7","Притч. 3:13-15","Притч. 3:19-22","Притч. 4:18-21","Притч. 12:1-3",
  ]},
];

function ScripturePickerModal({ onSelect, onClose, th }) {
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const inp = { width:"100%", background:th.inpBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, padding:"10px 14px", color:th.inpText, fontSize:15, outline:"none", boxSizing:"border-box" };
  const allRefs = BIBLE_REFS.flatMap(b => b.refs.map(r => ({ book: b.book, ref: r })));
  const filtered = search ? allRefs.filter(r => r.ref.toLowerCase().includes(search.toLowerCase())) : allRefs;

  if (preview) {
    const text = BIBLE_TEXTS[preview];
    return (
      <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}>
        <div style={{ background:th.modalBg, borderRadius:16, padding:20, width:"100%", maxWidth:520, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"90vh", display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ fontSize:17, fontWeight:800, color:th.modalTitle }}>{preview}</div>
          <div style={{ overflowY:"auto", flex:1, borderTop:`1px solid ${th.modalBorder}`, paddingTop:12 }}>
            {text
              ? <pre style={{ fontFamily:"Arial, sans-serif", fontSize:15, color:th.itemText, lineHeight:1.9, whiteSpace:"pre-wrap", margin:0 }}>{text}</pre>
              : <div style={{ color:th.lblColor, fontStyle:"italic" }}>Текст відсутній</div>
            }
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={()=>setPreview(null)} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"11px 0", flex:1, cursor:"pointer", fontSize:15 }}>← Назад</button>
            <button onClick={()=>onSelect(preview, text||"")} style={{ background:th.saveBg, color:th.saveText, border:"none", borderRadius:10, padding:"11px 0", flex:2, cursor:"pointer", fontSize:15, fontWeight:800 }}>Вибрати цей вірш</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}>
      <div style={{ background:th.modalBg, borderRadius:16, padding:20, width:"100%", maxWidth:440, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"85vh", display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ fontSize:16, fontWeight:800, color:th.modalTitle }}>Вірші для закличного читання</div>
        <input style={inp} placeholder="Пошук... напр. 90 або Притч" value={search} onChange={e=>setSearch(e.target.value)} autoFocus/>
        <div style={{ overflowY:"auto", flex:1 }}>
          {!search && BIBLE_REFS.map(b => (
            <div key={b.book}>
              <div style={{ fontSize:12, fontWeight:800, color:th.lblColor, padding:"8px 10px 4px", letterSpacing:1 }}>{b.book.toUpperCase()}</div>
              {b.refs.map(r => (
                <div key={r} onClick={()=>setPreview(r)}
                  style={{ padding:"7px 10px", borderRadius:8, cursor:"pointer", color:th.refText, fontSize:15, fontWeight:600 }}
                  onMouseEnter={e=>e.currentTarget.style.background=th.metaBarBg}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                >{r}</div>
              ))}
            </div>
          ))}
          {search && filtered.map(r => (
            <div key={r.ref} onClick={()=>setPreview(r.ref)}
              style={{ padding:"7px 10px", borderRadius:8, cursor:"pointer", color:th.refText, fontSize:15, fontWeight:600 }}
              onMouseEnter={e=>e.currentTarget.style.background=th.metaBarBg}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}
            >{r.ref}</div>
          ))}
        </div>
        <button onClick={onClose} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"10px 0", cursor:"pointer", fontSize:15 }}>Закрити</button>
      </div>
    </div>
  );
}

function ItemForm({ data, setData, th, psalmHistory }) {
  const [showPicker, setShowPicker] = useState(false);
  const [showScripturePicker, setShowScripturePicker] = useState(false);
  const lbl = { display:"block", color:th.lblColor, fontSize:13, marginBottom:6, marginTop:14 };
  const inp = { width:"100%", background:th.inpBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, padding:"10px 14px", color:th.inpText, fontSize:15, outline:"none", boxSizing:"border-box" };
  return <>
    <label style={lbl}>Назва пункту</label>
    <input style={inp} value={data.label||""} onChange={e=>setData({...data, label:e.target.value})}/>

    {data.type === "psalm" && <>
      <label style={lbl}>Номер псалму</label>
      <div style={{ display:"flex", gap:8 }}>
        <input style={{...inp, flex:1}} value={data.psalm||""} onChange={e=>setData({...data, psalm:e.target.value})} placeholder="напр. 40"/>
        <button type="button" onClick={()=>setShowPicker(true)} style={{ background:th.editBtnBg, border:th.editBtnBorder, borderRadius:8, padding:"0 14px", color:th.editBtnText, fontSize:13, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>Вибрати</button>
      </div>
      {data.suggestions && data.suggestions.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
          <span style={{ fontSize:12, color:th.lblColor, alignSelf:"center" }}>Варіанти:</span>
          {data.suggestions.map(num => {
            const s = PISNI_NADIYI.find(p => String(p.num) === String(num));
            const active = String(data.psalm) === String(num);
            return (
              <button key={num} type="button"
                onClick={()=>setData({...data, psalm:String(num), psalmName:s ? s.name : ""})}
                style={{ background: active ? th.saveBg : th.editBtnBg, color: active ? th.saveText : th.editBtnText,
                  border: active ? "none" : th.editBtnBorder, borderRadius:8, padding:"4px 12px", fontSize:13, cursor:"pointer" }}>
                №{num}{s ? ` ${s.name.slice(0,14)}…` : ""}
              </button>
            );
          })}
        </div>
      )}
      <label style={lbl}>Назва псалму</label>
      <input style={inp} value={data.psalmName||""} onChange={e=>setData({...data, psalmName:e.target.value})} placeholder="напр. «Шлях до спасіння»"/>
      <label style={lbl}><input type="checkbox" checked={!!data.video} onChange={e=>setData({...data,video:e.target.checked})}/> <span style={{marginLeft:8,color:th.checkboxText}}>Відео</span></label>
      {showPicker && <SongPickerModal onSelect={s=>{ setData({...data, psalm:String(s.num), psalmName:s.name}); setShowPicker(false); }} onClose={()=>setShowPicker(false)} th={th} history={psalmHistory}/>}
    </>}

    {(data.type === "scripture" || data.type === "blessing" || data.type === "offering_video") && <>
      <label style={lbl}>Посилання (напр. Пс. 47:10)</label>
      <div style={{ display:"flex", gap:8 }}>
        <input style={{...inp, flex:1}} value={data.ref||""} onChange={e=>setData({...data, ref:e.target.value})}/>
        {data.type === "scripture" && (
          <button type="button" onClick={()=>setShowScripturePicker(true)} style={{ background:th.editBtnBg, border:th.editBtnBorder, borderRadius:8, padding:"0 14px", color:th.editBtnText, fontSize:13, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>Вибрати</button>
        )}
      </div>
      <label style={lbl}>Текст</label>
      <textarea style={{...inp, height:100, resize:"vertical"}} value={data.text||""} onChange={e=>setData({...data, text:e.target.value})}/>
      {showScripturePicker && <ScripturePickerModal onSelect={(r,t)=>{ setData({...data, ref:r, text:t}); setShowScripturePicker(false); }} onClose={()=>setShowScripturePicker(false)} th={th}/>}
    </>}

    {data.type === "sermon" && <>
      <label style={lbl}>Тема проповіді</label>
      <input style={inp} value={data.topic||""} onChange={e=>setData({...data, topic:e.target.value})} placeholder="Введіть тему..."/>
    </>}

    {data.type === "blessing_person" && <>
      <label style={lbl}>Хто благословляє</label>
      <input style={inp} value={data.person||""} onChange={e=>setData({...data, person:e.target.value})}/>
    </>}

    {data.type === "announcements" && <>
      <label style={lbl}>Об'ява 1</label>
      <input style={inp} value={data.note||""}  onChange={e=>setData({...data, note:e.target.value})}  placeholder="Текст об'яви..."/>
      <label style={lbl}>Об'ява 2</label>
      <input style={inp} value={data.note2||""} onChange={e=>setData({...data, note2:e.target.value})} placeholder="Текст об'яви..."/>
      <label style={lbl}>Об'ява 3</label>
      <input style={inp} value={data.note3||""} onChange={e=>setData({...data, note3:e.target.value})} placeholder="Текст об'яви..."/>
      <label style={lbl}>Об'ява 4</label>
      <input style={inp} value={data.note4||""} onChange={e=>setData({...data, note4:e.target.value})} placeholder="Текст об'яви..."/>
    </>}

    {data.type === "greetings" && <>
      <label style={lbl}>Примітка</label>
      <textarea style={{...inp, height:80, resize:"vertical"}} value={data.note||""} onChange={e=>setData({...data, note:e.target.value})} placeholder="Додаткова інформація..."/>
    </>}

    {data.type === "music" && <>
      <label style={lbl}>Виконавець</label>
      <input style={inp} value={data.performer||""} onChange={e=>setData({...data, performer:e.target.value})} placeholder="ПІБ або назва гурту"/>
    </>}

    {data.type === "services_schedule" && <>
      <label style={lbl}>П'ятниця</label>
      <input style={inp} value={data.friday||""} onChange={e=>setData({...data, friday:e.target.value})} placeholder="напр. 19:00"/>
      <label style={lbl}>Субота ранок</label>
      <input style={inp} value={data.satMorning||""} onChange={e=>setData({...data, satMorning:e.target.value})} placeholder="напр. 10:00"/>
      <label style={lbl}>Субота після обіду</label>
      <input style={inp} value={data.satAfternoon||""} onChange={e=>setData({...data, satAfternoon:e.target.value})} placeholder="напр. 14:00"/>
    </>}
  </>;
}

function EditModal({ item, onSave, onClose, th, psalmHistory }) {
  const [data, setData] = useState({ ...item });
  return (
    <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, padding:16 }}>
      <div style={{ background:th.modalBg, borderRadius:16, padding:28, width:"100%", maxWidth:560, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ fontSize:18, fontWeight:800, color:th.modalTitle, marginBottom:20 }}>Редагувати пункт</div>
        <ItemForm data={data} setData={setData} th={th} psalmHistory={psalmHistory}/>
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={()=>onSave(data)} style={{ flex:1, background:th.saveBg, color:th.saveText, border:"none", borderRadius:10, padding:"12px 0", fontSize:16, fontWeight:800, cursor:"pointer" }}>Зберегти</button>
          <button onClick={onClose} style={{ flex:1, background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"12px 0", fontSize:16, cursor:"pointer" }}>Скасувати</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ onSave, onClose, th, psalmHistory }) {
  const [type, setType] = useState("prayer");
  const [data, setData] = useState({ type: "prayer", ...TYPE_DEFAULTS["prayer"] });
  const lbl = { display:"block", color:th.lblColor, fontSize:13, marginBottom:6, marginTop:14 };
  const inp = { width:"100%", background:th.inpBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, padding:"10px 14px", color:th.inpText, fontSize:15, outline:"none", boxSizing:"border-box" };

  function handleTypeChange(e) {
    const t = e.target.value;
    setType(t);
    setData({ type: t, ...TYPE_DEFAULTS[t] });
  }

  return (
    <div style={{ position:"fixed", inset:0, background:th.modalOverlay, display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, padding:16 }}>
      <div style={{ background:th.modalBg, borderRadius:16, padding:28, width:"100%", maxWidth:560, border:`1px solid ${th.modalBorder}`, boxShadow:th.modalShadow, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ fontSize:18, fontWeight:800, color:th.modalTitle, marginBottom:20 }}>Додати пункт</div>
        <label style={lbl}>Тип пункту</label>
        <select style={inp} value={type} onChange={handleTypeChange}>
          {Object.entries(TYPE_LABELS).map(([k,v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <ItemForm data={data} setData={setData} th={th} psalmHistory={psalmHistory}/>
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={()=>onSave({ ...data, id: Date.now() })} style={{ flex:1, background:th.saveBg, color:th.saveText, border:"none", borderRadius:10, padding:"12px 0", fontSize:16, fontWeight:800, cursor:"pointer" }}>Додати</button>
          <button onClick={onClose} style={{ flex:1, background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"12px 0", fontSize:16, cursor:"pointer" }}>Скасувати</button>
        </div>
      </div>
    </div>
  );
}

function PrintView({ tab, items, meta }) {
  const tmpl = TEMPLATES[tab];
  const today = new Date();
  const printDate = `${today.getDate()} ${UA_MONTHS_FULL[today.getMonth()]} ${today.getFullYear()}`;

  return (
    <div className="print-area" style={{ fontFamily:"Georgia, 'Times New Roman', serif", color:"#000", background:"#fff" }}>

      {/* Шапка */}
      <div style={{ textAlign:"center", borderBottom:"3px double #000", paddingBottom:10, marginBottom:14 }}>
        <div style={{ fontSize:11, letterSpacing:3, textTransform:"uppercase", color:"#555", marginBottom:4 }}>Церква АСД</div>
        <div style={{ fontSize:24, fontWeight:900, letterSpacing:2, textTransform:"uppercase" }}>ХІД СЛУЖІННЯ</div>
        <div style={{ fontSize:15, fontWeight:700, marginTop:4, letterSpacing:1 }}>{tmpl.label}</div>
      </div>

      {/* Дата та ведучий */}
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14, fontSize:13, borderBottom:"1px solid #ccc", paddingBottom:8, flexWrap:"wrap", gap:8 }}>
        <span>{meta.date ? `Дата: ${formatDateUkr(meta.date)}` : `Дата: ____________________`}</span>
        <span>{meta.leader ? `Ведучий: ${meta.leader}` : `Ведучий: ____________________`}</span>
        {meta.preacher && <span>{`Проповідуючий: ${meta.preacher}`}</span>}
      </div>

      {/* Пункти */}
      {items.map((item, i) => (
        <div key={item.id} style={{ marginBottom:8, paddingBottom:7, borderBottom:"1px solid #e0e0e0", pageBreakInside:"avoid" }}>

          <div style={{ display:"flex", gap:8, alignItems:"baseline" }}>
            <span style={{ fontSize:17, fontWeight:900, minWidth:28 }}>{i+1}.</span>
            <span style={{ fontSize:18, fontWeight:700 }}>{item.label}</span>
          </div>

          {item.psalm && (
            <div style={{ fontSize:15, marginTop:3, marginLeft:36, color:"#222" }}>
              №{item.psalm}{item.psalmName ? ` «${item.psalmName}»` : ""}{item.video ? "  [відео]" : ""}
            </div>
          )}

          {item.topic && (
            <div style={{ fontSize:15, marginTop:4, marginLeft:36, fontStyle:"italic", borderLeft:"3px solid #999", paddingLeft:9 }}>
              «{item.topic}»
            </div>
          )}

          {item.performer && (
            <div style={{ fontSize:15, marginTop:3, marginLeft:36, color:"#333" }}>
              Виконавець: {item.performer}
            </div>
          )}

          {item.person && (
            <div style={{ fontSize:15, marginTop:3, marginLeft:36, color:"#333" }}>{item.person}</div>
          )}

          {item.ref && (
            <div style={{ marginTop:5, marginLeft:36, paddingLeft:9, borderLeft:"3px solid #aaa" }}>
              <div style={{ fontSize:15, fontWeight:800 }}>{item.ref}</div>
              {item.text && <div style={{ fontSize:14, fontStyle:"italic", color:"#333", lineHeight:1.6, marginTop:2 }}>{item.text}</div>}
            </div>
          )}

          {item.type === "services_schedule" && (item.friday || item.satMorning || item.satAfternoon) && (
            <div style={{ fontSize:15, marginTop:4, marginLeft:36, lineHeight:1.8 }}>
              {item.friday       && <div>П'ятниця — {item.friday}</div>}
              {item.satMorning   && <div>Субота ранок — {item.satMorning}</div>}
              {item.satAfternoon && <div>Субота після обіду — {item.satAfternoon}</div>}
            </div>
          )}

          {item.type === "announcements" && (
            <div style={{ fontSize:15, marginTop:4, marginLeft:36, lineHeight:1.8 }}>
              {[item.note, item.note2, item.note3, item.note4].filter(Boolean).map((n, j) => (
                <div key={j}>{j+1}) {n}</div>
              ))}
            </div>
          )}

          {item.type !== "announcements" && item.note && (
            <div style={{ fontSize:15, marginTop:3, marginLeft:36, color:"#444" }}>{item.note}</div>
          )}
        </div>
      ))}

      {/* Підвал */}
      <div style={{ marginTop:16, paddingTop:6, borderTop:"1px solid #ccc", fontSize:10, color:"#888", textAlign:"right" }}>
        Роздруковано: {printDate}
      </div>
    </div>
  );
}

function PersonAutocomplete({ id, value, onChange, onSave, onClear, history, placeholder, th, inp }) {
  const [show, setShow] = useState(false);
  const sugg = (history || []).filter(n => !value || n.toLowerCase().includes(value.toLowerCase()));
  return (
    <div style={{ position:"relative" }}>
      <input
        id={id}
        style={inp}
        value={value}
        autoComplete="off"
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setShow(true)}
        onBlur={() => { setTimeout(() => setShow(false), 150); onSave && onSave(value); }}
      />
      {show && sugg.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:200, background:th.modalBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.18)", overflow:"hidden", marginTop:2 }}>
          {sugg.map(name => (
            <div
              key={name}
              onMouseDown={() => { onChange(name); setShow(false); }}
              style={{ padding:"10px 14px", cursor:"pointer", color:th.inpText, fontSize:14, borderBottom:`1px solid ${th.inpBorder}` }}
              onMouseEnter={e => e.currentTarget.style.background = th.metaBarBg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              👤 {name}
            </div>
          ))}
          <div
            onMouseDown={() => { onClear && onClear(); setShow(false); }}
            style={{ padding:"8px 14px", cursor:"pointer", color:th.deleteBtnText, fontSize:12, textAlign:"center", opacity:0.8 }}
            onMouseEnter={e => e.currentTarget.style.opacity = "1"}
            onMouseLeave={e => e.currentTarget.style.opacity = "0.8"}
          >
            🗑 Очистити список
          </div>
        </div>
      )}
    </div>
  );
}

function MetaBar({ meta, onChange, viewMode, th, leaderHistory, onLeaderSave, onLeaderClear, preacherHistory, onPreacherSave, onPreacherClear }) {
  const inp = { background:th.inpBg, border:`1px solid ${th.inpBorder}`, borderRadius:8, padding:"8px 12px", color:th.inpText, fontSize:14, outline:"none", width:"100%", boxSizing:"border-box" };
  const lbl = { display:"block", color:th.lblColor, fontSize:12, marginBottom:4 };

  if (viewMode) {
    if (!meta.date && !meta.leader && !meta.preacher) return null;
    return (
      <div style={{ display:"flex", gap:24, marginBottom:14, padding:"10px 16px", background:th.metaBarBg, borderRadius:10, border:`1px solid ${th.metaBarBorder}`, flexWrap:"wrap" }}>
        {meta.date    && <div style={{ fontSize:16, fontWeight:700, color:th.metaValueColor }}>📅 {formatDateUkr(meta.date)}</div>}
        {meta.leader  && <div style={{ fontSize:16, fontWeight:700, color:th.metaValueColor }}>👤 {meta.leader}</div>}
        {meta.preacher && <div style={{ fontSize:16, fontWeight:700, color:th.metaValueColor }}>🎤 {meta.preacher}</div>}
      </div>
    );
  }

  return (
    <div style={{ display:"flex", gap:12, marginBottom:12, flexWrap:"wrap" }}>
      <div style={{ flex:1, minWidth:160 }}>
        <label htmlFor="meta-date" style={lbl}>Дата служіння</label>
        <input
          id="meta-date"
          type="date"
          style={{ ...inp, colorScheme:"light dark", cursor:"pointer" }}
          value={meta.date}
          onChange={e => onChange({...meta, date:e.target.value})}
          onClick={e => { try { e.target.showPicker(); } catch {} }}
        />
      </div>
      <div style={{ flex:1, minWidth:160 }}>
        <label htmlFor="meta-leader" style={lbl}>Ведучий</label>
        <PersonAutocomplete id="meta-leader" value={meta.leader} onChange={v=>onChange({...meta,leader:v})} onSave={onLeaderSave} onClear={onLeaderClear} history={leaderHistory} placeholder="ПІБ ведучого" th={th} inp={inp}/>
      </div>
      <div style={{ flex:1, minWidth:160 }}>
        <label htmlFor="meta-preacher" style={lbl}>Проповідуючий</label>
        <PersonAutocomplete id="meta-preacher" value={meta.preacher||""} onChange={v=>onChange({...meta,preacher:v})} onSave={onPreacherSave} onClear={onPreacherClear} history={preacherHistory} placeholder="ПІБ проповідуючого" th={th} inp={inp}/>
      </div>
    </div>
  );
}

function ServiceItem({ item, idx, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast, viewMode, th, isMobile, onShowLyrics }) {
  const c = th.typeColors[item.type] || th.typeColors.default;
  const hasFields = !["prayer","offering"].includes(item.type);
  const moveBtn = (label, handler, disabled) => (
    <button onClick={handler} disabled={disabled} style={{ background:th.moveBtnBg, border:th.moveBtnBorder, borderRadius:8, padding:"4px 10px", color:th.moveBtnText, fontSize:13, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.25 : 1 }}>{label}</button>
  );

  const itemContent = (
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:viewMode?22:17, fontWeight:700, color:th.itemText, lineHeight:1.3 }}>{item.label}</div>
      {item.psalm && <div
        onClick={viewMode && item.psalm ? ()=>onShowLyrics({psalm:item.psalm, psalmName:item.psalmName}) : undefined}
        style={{ fontSize:viewMode?18:14, color:th.psalmText, marginTop:3, cursor: viewMode && item.psalm ? "pointer" : "default",
          ...(viewMode && item.psalm ? { textDecoration:"underline dotted", textUnderlineOffset:3 } : {})
        }}>
        №{item.psalm} {item.psalmName}
        {item.video && <span style={{ background:th.videoBadgeBg, color:th.videoBadgeText, borderRadius:5, padding:"1px 8px", fontSize:12, marginLeft:6 }}>▶ відео</span>}
        {viewMode && item.psalm && PISNI_TEXTS[item.psalm] && <span style={{ fontSize:13, marginLeft:8, opacity:0.6 }}>📖</span>}
      </div>}
      {item.topic && <div style={{ fontSize:viewMode?17:14, color:th.topicText, fontStyle:"italic", marginTop:3 }}>«{item.topic}»</div>}
      {item.person    && <div style={{ fontSize:viewMode?16:13, color:th.personText, fontStyle:"italic", marginTop:2 }}>{item.person}</div>}
      {item.performer && <div style={{ fontSize:viewMode?16:13, color:th.personText, fontStyle:"italic", marginTop:2 }}>🎵 {item.performer}</div>}
      {item.ref && <div
        onClick={viewMode && item.text ? ()=>onShowLyrics({type:"scripture", ref:item.ref, text:item.text}) : undefined}
        style={{ marginTop:6, borderLeft:`2px solid ${th.refBorder}`, paddingLeft:10,
          cursor: viewMode && item.text ? "pointer" : "default" }}>
        <div style={{ fontSize:viewMode?15:12, fontWeight:800, color:th.refText }}>
          {item.ref}
          {viewMode && item.text && <span style={{ fontSize:12, marginLeft:8, opacity:0.6 }}>📖</span>}
        </div>
        <div style={{ fontSize:viewMode?16:13, color:th.refBody, fontStyle:"italic", lineHeight:1.6, marginTop:2 }}>{item.text}</div>
      </div>}
      {item.type === "announcements"
        ? [item.note, item.note2, item.note3, item.note4].filter(Boolean).map((n, i) => (
            <div key={i} style={{ fontSize:viewMode?15:13, color:th.noteText, marginTop:4, background:th.noteBg, borderRadius:6, padding:"4px 10px", display:"block" }}>{n}</div>
          ))
        : item.note && <div style={{ fontSize:viewMode?15:13, color:th.noteText, marginTop:4, background:th.noteBg, borderRadius:6, padding:"4px 10px", display:"inline-block" }}>{item.note}</div>
      }
      {item.type === "services_schedule" && (item.friday || item.satMorning || item.satAfternoon) && (
        <div style={{ marginTop:6, fontSize:viewMode?16:13, color:th.refBody, lineHeight:1.8 }}>
          {item.friday      && <div>П'ятниця — {item.friday}</div>}
          {item.satMorning  && <div>Субота ранок — {item.satMorning}</div>}
          {item.satAfternoon && <div>Субота після обіду — {item.satAfternoon}</div>}
        </div>
      )}
    </div>
  );

  const desktopButtons = !viewMode && (
    <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
      {moveBtn("↑", onMoveUp, isFirst)}
      {hasFields && <button onClick={()=>onEdit(item)} style={{ background:th.editBtnBg, border:th.editBtnBorder, borderRadius:8, padding:"6px 10px", color:th.editBtnText, fontSize:13, cursor:"pointer" }}>✏️</button>}
      {moveBtn("↓", onMoveDown, isLast)}
    </div>
  );

  const mobileButtons = !viewMode && (
    <div style={{ display:"flex", gap:6, marginTop:10, justifyContent:"flex-end" }}>
      {moveBtn("↑", onMoveUp, isFirst)}
      {moveBtn("↓", onMoveDown, isLast)}
      {hasFields && <button onClick={()=>onEdit(item)} style={{ background:th.editBtnBg, border:th.editBtnBorder, borderRadius:8, padding:"5px 12px", color:th.editBtnText, fontSize:13, cursor:"pointer" }}>✏️</button>}
    </div>
  );

  return (
    <div style={{ padding:"12px 14px", borderRadius:12, background:c.bg, borderLeft:`4px solid ${c.border}`, marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
        <div style={{ minWidth:36, height:36, background:c.num, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:c.numText, fontWeight:900, flexShrink:0 }}>{idx}</div>
        {itemContent}
        {!isMobile && desktopButtons}
      </div>
      {isMobile && mobileButtons}
      {!viewMode && (
        <div style={{ marginTop:8, display:"flex", justifyContent:"flex-start" }}>
          <button onClick={()=>onDelete(item.id)} style={{ background:th.deleteBtnBg, border:th.deleteBtnBorder, borderRadius:8, padding:"3px 12px", color:th.deleteBtnText, fontSize:12, cursor:"pointer" }}>🗑</button>
        </div>
      )}
    </div>
  );
}

function HelpModal({ onClose, th }) {
  const overlay = { position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 };
  const box = { background:th.modalBg, border:`1px solid ${th.modalBorder}`, borderRadius:16, boxShadow:th.modalShadow, width:"100%", maxWidth:560, maxHeight:"88vh", display:"flex", flexDirection:"column" };
  const h2 = { color:th.modalTitle, fontSize:20, fontWeight:900, margin:0 };
  const section = { marginBottom:18 };
  const title = { color:th.modalTitle, fontSize:14, fontWeight:800, marginBottom:6, borderBottom:`1px solid ${th.inpBorder}`, paddingBottom:4 };
  const row = { display:"flex", gap:8, alignItems:"flex-start", marginBottom:6, fontSize:14, color:th.inpText, lineHeight:1.5 };
  const badge = { flexShrink:0, fontSize:16 };
  const note = { fontSize:12, color:th.lblColor, marginTop:3 };

  const sections = [
    {
      heading: "Два режими роботи",
      items: [
        { icon:"✏️", text:"Редагування — режим підготовки. Тут заповнюєш усі дані перед служінням." },
        { icon:"📋", text:"Кафедра — великий шрифт для читання з екрана під час служіння. Кнопки редагування зникають, натисни псалм або вірш щоб побачити повний текст на весь екран." },
      ]
    },
    {
      heading: "Вкладки (5 типів служінь)",
      items: [
        { icon:"🗂️", text:"П'ЯТНИЦЯ ВЕЧІР · СУБОТНЯ ШКОЛА · СУБОТА РАНОК · СУБОТА ПІСЛЯ ОБІДУ · ВЕЧЕРЯ ГОСПОДНЯ. Кожна вкладка — окремий шаблон зі своїм набором пунктів і своїми даними." },
      ]
    },
    {
      heading: "Кнопки на картці пункту",
      items: [
        { icon:"✏️", text:"Редагувати пункт — відкриває вікно де можна змінити псалм, тему, вірш, виконавця тощо." },
        { icon:"↑ ↓", text:"Перемістити пункт вгору або вниз у списку." },
        { icon:"🗑", text:"Видалити пункт (кнопка в лівому нижньому куті картки)." },
      ]
    },
    {
      heading: "Як заповнити псалом (пісню)",
      items: [
        { icon:"1", text:"Натисни ✏️ на пункті типу «Псалом»." },
        { icon:"2", text:"У вікні редагування клікни поле номера — відкриється пікер пісень." },
        { icon:"3", text:"Введи номер або частину назви в пошук, обери зі списку." },
        { icon:"✓", text:"Позначка ✓ біля номера = цей псалом вже співали за останні 2 місяці. Краще обрати без позначки." },
        { icon:"▶", text:"Галочка «відео» — якщо псалом буде показано з відео-супроводом." },
      ]
    },
    {
      heading: "Як вибрати вірш з Біблії",
      items: [
        { icon:"1", text:"Натисни ✏️ на пункті типу «Вірш з Біблії» або «Читання перед збором»." },
        { icon:"2", text:"Натисни кнопку «Обрати з Біблії» — відкриється список 87 готових посилань (Псалми, Притчі)." },
        { icon:"3", text:"Обери посилання — текст підставиться автоматично. Або введи своє посилання і текст вручну." },
        { icon:"📖", text:"В режимі Кафедра натисни на вірш щоб побачити текст на весь екран." },
      ]
    },
    {
      heading: "Додавання і порядок пунктів",
      items: [
        { icon:"＋", text:"«+ Додати пункт» внизу сторінки — обираєш тип (молитва, псалм, проповідь, об'яви тощо) і він з'являється в кінці списку." },
        { icon:"↑↓", text:"Переміщуй кнопками ↑ ↓ на потрібне місце." },
        { icon:"↺", text:"«Скинути шаблон» — повертає стандартний набір пунктів для цього типу служіння." },
      ]
    },
    {
      heading: "Власний шаблон громади",
      items: [
        { icon:"💾", text:"«Зберегти шаблон» — зберігає поточний набір пунктів (їх порядок, назви, об'яви і розклад) як шаблон для цього типу служіння. Псалми, теми, вірші та виконавці очищуються." },
        { icon:"↺", text:"«Скинути (власний)» — повертає список до збереженого шаблону. Якщо власного немає — до стандартного." },
        { icon:"✕", text:"«✕ власний» — видаляє власний шаблон і відновлює стандартний." },
        { icon:"💡", text:"Приклад: якщо у вашій громаді інший порядок пунктів або постійний розклад богослужінь — налаштуй, збережи як шаблон, і щотижня починай зі свого варіанту." },
      ]
    },
    {
      heading: "Архів служінь",
      items: [
        { icon:"📦", text:"Кнопка 📦 з'являється коли заповнені: дата + ведучий + проповідник + хоча б один псалом. Натисни — служіння збережеться в архів." },
        { icon:"🗄️", text:"Кнопка 🗄️ відкриває архів: список усіх збережених служінь і статистика топ-псалмів." },
      ]
    },
    {
      heading: "Інші інструменти",
      items: [
        { icon:"🖨️", text:"Друк або збереження в PDF — акуратна версія для роздруківки." },
        { icon:"📤", text:"Експорт JSON — зберігає всі дані у файл (резервна копія)." },
        { icon:"📥", text:"Імпорт JSON — відновлює дані з файлу." },
        { icon:"🌙☀️", text:"Перемикач між темною і світлою темою." },
      ]
    },
  ];

  return (
    <div style={overlay} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={box}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 20px 14px", borderBottom:`1px solid ${th.inpBorder}`, flexShrink:0 }}>
          <h2 style={h2}>❓ Довідка — ХІД СЛУЖІННЯ</h2>
          <button onClick={onClose} style={{ background:"transparent", border:"none", fontSize:22, cursor:"pointer", color:th.lblColor, lineHeight:1 }}>✕</button>
        </div>
        <div style={{ overflowY:"auto", padding:"18px 20px 20px", flex:1 }}>
          {sections.map(s => (
            <div key={s.heading} style={section}>
              <div style={title}>{s.heading}</div>
              {s.items.map((it, i) => (
                <div key={i} style={row}>
                  <span style={badge}>{it.icon}</span>
                  <span>{it.text}</span>
                </div>
              ))}
            </div>
          ))}
          <div style={{ ...note, textAlign:"center", marginTop:8 }}>Всі дані зберігаються автоматично в браузері.</div>
        </div>
      </div>
    </div>
  );
}

function ArchiveModal({ archive, onDelete, onClose, th }) {
  const [filter, setFilter] = useState("all");
  const filterLabels = [
    ["all", "Всі"],
    ["friday", "П'ЯТНИЦЯ"],
    ["satSchool", "ШКОЛА"],
    ["satMorning", "СБТ РАНОК"],
    ["satAfternoon", "ПІСЛЯ ОБІДУ"],
    ["communion", "ВЕЧЕРЯ"],
  ];
  const filtered = filter === "all" ? archive : archive.filter(e => e.serviceType === filter);

  const psalmCount = {};
  archive.forEach(e => {
    e.psalms.forEach(p => {
      if (!psalmCount[p.num]) psalmCount[p.num] = { count: 0, name: p.name };
      psalmCount[p.num].count++;
      if (!psalmCount[p.num].name && p.name) psalmCount[p.num].name = p.name;
    });
  });
  const topPsalms = Object.entries(psalmCount)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 5);

  return (
    <div style={{ position:"fixed", inset:0, background:th.modalOverlay, zIndex:400, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"20px 8px" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:th.modalBg, border:`1px solid ${th.modalBorder}`, borderRadius:16, boxShadow:th.modalShadow, width:"100%", maxWidth:540, maxHeight:"90vh", display:"flex", flexDirection:"column", padding:"20px 20px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:18, fontWeight:800, color:th.titleColor }}>🗄️ Архів служінь</div>
          <button onClick={onClose} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:8, padding:"6px 14px", fontWeight:700, fontSize:14, cursor:"pointer" }}>✕</button>
        </div>

        {archive.length === 0 ? (
          <div style={{ color:th.tabUnselectedText, textAlign:"center", padding:"32px 0", fontSize:15 }}>Архів порожній</div>
        ) : <>
          {topPsalms.length > 0 && (
            <div style={{ marginBottom:14, padding:"10px 14px", background:th.metaBarBg, borderRadius:8, border:`1px solid ${th.metaBarBorder}` }}>
              <div style={{ fontSize:11, fontWeight:700, color:th.lblColor, marginBottom:6, letterSpacing:1 }}>ТОП ПСАЛМІВ:</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {topPsalms.map(([num, {count, name}]) => (
                  <span key={num} style={{ fontSize:13, color:th.psalmText }}>
                    №{num}{name ? ` «${name}»` : ""} <span style={{ fontWeight:700, background:th.editBtnBg, borderRadius:4, padding:"1px 5px", fontSize:11, color:th.metaValueColor }}>×{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
            {filterLabels.map(([k, lbl]) => {
              const count = k === "all" ? archive.length : archive.filter(e=>e.serviceType===k).length;
              if (k !== "all" && count === 0) return null;
              return (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:11,
                  background: filter===k ? th.saveBg : th.editBtnBg,
                  color: filter===k ? th.saveText : th.editBtnText,
                  fontWeight: filter===k ? 700 : 400,
                }}>{lbl} ({count})</button>
              );
            })}
          </div>

          <div style={{ overflowY:"auto", flex:1 }}>
            {filtered.length === 0 && <div style={{ color:th.tabUnselectedText, textAlign:"center", padding:20 }}>Немає записів</div>}
            {filtered.map(entry => (
              <div key={entry.id} style={{ padding:"11px 14px", marginBottom:8, borderRadius:10, background:th.editBtnBg, border:`1px solid ${th.modalBorder}`, position:"relative" }}>
                <div style={{ fontWeight:700, color:th.metaValueColor, fontSize:14, marginBottom:4, paddingRight:30 }}>
                  {formatDateUkr(entry.date)} · <span style={{ fontSize:11, opacity:0.7, fontWeight:400 }}>{entry.serviceTypeName}</span>
                </div>
                <div style={{ fontSize:13, color:th.itemText }}>
                  👤 {entry.leader} · 🎤 {entry.preacher}
                </div>
                {entry.psalms.length > 0 && (
                  <div style={{ fontSize:12, color:th.psalmText, marginTop:4 }}>
                    {entry.psalms.map(p => `№${p.num}${p.name ? ` «${p.name}»` : ""}`).join(" · ")}
                  </div>
                )}
                <button onClick={() => { if (window.confirm("Видалити цей запис?")) onDelete(entry.id); }}
                  style={{ position:"absolute", top:10, right:10, background:"transparent", border:"none", color:th.deleteBtnText, cursor:"pointer", fontSize:15, padding:"2px 6px" }}>
                  🗑
                </button>
              </div>
            ))}
          </div>
        </>}

        <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
          <button onClick={onClose} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, cursor:"pointer", fontSize:14 }}>Закрити</button>
        </div>
      </div>
    </div>
  );
}

function LyricsModal({ item, onClose, th }) {
  const isScripture = item.type === "scripture";
  const title = isScripture ? item.ref : `№${item.psalm} ${item.psalmName}`;
  const text   = isScripture ? item.text : PISNI_TEXTS[item.psalm];
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:th.pageBg, zIndex:300, display:"flex", flexDirection:"column", padding:"20px 16px 16px" }}>
      <div onClick={e=>e.stopPropagation()} style={{ display:"flex", flexDirection:"column", flex:1, minHeight:0 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12, gap:12 }}>
          <div style={{ fontSize:20, fontWeight:900, color:th.titleColor, lineHeight:1.3 }}>{title}</div>
          <button onClick={onClose} style={{ background:th.cancelBg, color:th.cancelText, border:"none", borderRadius:10, padding:"8px 18px", fontSize:15, cursor:"pointer", flexShrink:0 }}>✕ Закрити</button>
        </div>
        <div style={{ overflowY:"auto", flex:1 }}>
          {text
            ? <pre style={{ fontFamily:"Arial, sans-serif", fontSize:18, color:th.itemText, lineHeight:2, whiteSpace:"pre-wrap", margin:0 }}>{text}</pre>
            : <div style={{ color:th.lblColor, fontStyle:"italic", fontSize:16 }}>Текст відсутній</div>
          }
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const isMobile = useIsMobile();
  const [themeName, setThemeName] = useState(() => localStorage.getItem("theme") || "light");
  const [tab, setTab] = useState("friday");
  const tabRefs = useRef({});
  const importRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [services, setServices] = useState(() => {
    const saved = {};
    ["friday","satSchool","satMorning","satAfternoon","communion"].forEach(k => {
      try {
        const s = localStorage.getItem("service_"+k);
        saved[k] = s ? JSON.parse(s) : TEMPLATES[k].items.map(i=>({...i}));
      } catch { saved[k] = TEMPLATES[k].items.map(i=>({...i})); }
    });
    return saved;
  });
  const [meta, setMeta] = useState(() => {
    const m = {};
    ["friday","satSchool","satMorning","satAfternoon","communion"].forEach(k => {
      try {
        const s = localStorage.getItem("meta_"+k);
        m[k] = s ? JSON.parse(s) : { date: "", leader: "" };
      } catch { m[k] = { date: "", leader: "" }; }
    });
    return m;
  });
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [lyricsItem, setLyricsItem] = useState(null);
  const [psalmHistory, setPsalmHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("psalm_history")) || {}; } catch { return {}; }
  });
  const [leaderHistory, setLeaderHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("leader_history")) || []; } catch { return []; }
  });
  const [preacherHistory, setPreacherHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("preacher_history")) || []; } catch { return []; }
  });
  const [archiveHistory, setArchiveHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sluzhinnya_archive")) || []; } catch { return []; }
  });
  const [showArchive, setShowArchive] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [customTemplates, setCustomTemplates] = useState(() => {
    const r = {};
    ["friday","satSchool","satMorning","satAfternoon","communion"].forEach(k => {
      try { const s = localStorage.getItem("sluzhinnya_template_"+k); if (s) r[k] = JSON.parse(s); } catch {}
    });
    return r;
  });

  useEffect(() => {
    try { localStorage.setItem("leader_history", JSON.stringify(leaderHistory)); } catch {}
  }, [leaderHistory]);
  useEffect(() => {
    try { localStorage.setItem("preacher_history", JSON.stringify(preacherHistory)); } catch {}
  }, [preacherHistory]);

  useEffect(() => {
    if (!menuOpen) return;
    function close(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  function handleLeaderSave(name) {
    if (!name || !name.trim()) return;
    setLeaderHistory(h => [name, ...h.filter(n => n !== name)].slice(0, 10));
  }
  function handlePreacherSave(name) {
    if (!name || !name.trim()) return;
    setPreacherHistory(h => [name, ...h.filter(n => n !== name)].slice(0, 10));
  }
  function handleLeaderClear() { setLeaderHistory([]); }
  function handlePreacherClear() { setPreacherHistory([]); }

  function getTemplateItems(t) {
    if (customTemplates[t]) return customTemplates[t].map(i => ({...i}));
    return TEMPLATES[t].items.map(i => ({...i}));
  }

  function handleSaveTemplate() {
    if (!window.confirm(`Зберегти поточний набір пунктів як шаблон для «${TEMPLATES[tab].label}»?\n\nПсалми, теми, виконавці та вірші будуть очищені — залишаться структура, назви пунктів, об'яви та розклад.`)) return;
    const CONTENT_FIELDS = ["psalm","psalmName","ref","text","topic","performer","person"];
    const templateItems = items.map(item => {
      const clean = {...item};
      CONTENT_FIELDS.forEach(f => { if (f in clean) clean[f] = f === "video" ? false : ""; });
      clean.video = false;
      return clean;
    });
    setCustomTemplates(prev => ({ ...prev, [tab]: templateItems }));
    try { localStorage.setItem("sluzhinnya_template_"+tab, JSON.stringify(templateItems)); } catch {}
    window.alert("✅ Шаблон збережено!");
  }

  function handleDeleteCustomTemplate() {
    if (!window.confirm(`Видалити власний шаблон для «${TEMPLATES[tab].label}»?\nБуде відновлено стандартний шаблон.`)) return;
    setCustomTemplates(prev => { const n = {...prev}; delete n[tab]; return n; });
    try { localStorage.removeItem("sluzhinnya_template_"+tab); } catch {}
  }

  function canArchive() {
    const m = meta[tab];
    return !!(m.date && m.leader && m.preacher && services[tab].some(i => i.type === "psalm" && i.psalm));
  }

  function handleArchive() {
    const m = meta[tab];
    const psalms = services[tab]
      .filter(i => i.type === "psalm" && i.psalm)
      .map(i => ({ num: i.psalm, name: i.psalmName || "" }));
    const entry = {
      id: Date.now().toString(),
      archivedAt: new Date().toISOString(),
      serviceType: tab,
      serviceTypeName: TEMPLATES[tab].label,
      date: m.date,
      leader: m.leader,
      preacher: m.preacher,
      psalms,
    };
    const updated = [entry, ...archiveHistory];
    setArchiveHistory(updated);
    try { localStorage.setItem("sluzhinnya_archive", JSON.stringify(updated)); } catch {}
    if (window.confirm("✅ Служіння архівовано!\n\nСкинути до шаблону для нового служіння?")) {
      setServices(s => ({ ...s, [tab]: getTemplateItems(tab) }));
      setMeta(prev => ({ ...prev, [tab]: { date: "", leader: "", preacher: "" } }));
    }
  }

  function handleDeleteArchiveEntry(id) {
    const updated = archiveHistory.filter(e => e.id !== id);
    setArchiveHistory(updated);
    try { localStorage.setItem("sluzhinnya_archive", JSON.stringify(updated)); } catch {}
  }

  function handleExport() {
    const data = { version: 1, exported: new Date().toISOString(), services, meta };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sluzhinnya-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.services || !data.meta) { alert("Невірний формат файлу"); return; }
        if (!window.confirm("Замінити всі поточні дані даними з файлу?")) return;
        setServices(data.services);
        setMeta(data.meta);
      } catch { alert("Помилка читання файлу"); }
    };
    reader.readAsText(file);
  }

  const th = THEMES[themeName];

  function markPsalmUsed(psalmNum) {
    if (!psalmNum) return;
    const updated = { ...psalmHistory, [String(psalmNum)]: new Date().toISOString().slice(0,10) };
    setPsalmHistory(updated);
    try { localStorage.setItem("psalm_history", JSON.stringify(updated)); } catch {}
  }

  function toggleTheme() {
    const next = themeName === "light" ? "dark" : "light";
    setThemeName(next);
    localStorage.setItem("theme", next);
  }

  useEffect(() => {
    Object.entries(services).forEach(([k,v]) => {
      try { localStorage.setItem("service_"+k, JSON.stringify(v)); } catch {}
    });
  }, [services]);

  useEffect(() => {
    Object.entries(meta).forEach(([k,v]) => {
      try { localStorage.setItem("meta_"+k, JSON.stringify(v)); } catch {}
    });
  }, [meta]);

  const items = services[tab];

  function handleSave(updated) {
    if (updated.type === "psalm" && updated.psalm) markPsalmUsed(updated.psalm);
    setServices(s => ({ ...s, [tab]: s[tab].map(i => i.id===updated.id ? updated : i) }));
    setEditing(null);
  }

  function handleAdd(newItem) {
    if (newItem.type === "psalm" && newItem.psalm) markPsalmUsed(newItem.psalm);
    setServices(s => ({ ...s, [tab]: [...s[tab], newItem] }));
    setAdding(false);
  }

  function handleMove(id, dir) {
    setServices(s => {
      const list = [...s[tab]];
      const idx = list.findIndex(i => i.id === id);
      const to = idx + dir;
      if (to < 0 || to >= list.length) return s;
      [list[idx], list[to]] = [list[to], list[idx]];
      return { ...s, [tab]: list };
    });
  }

  function handleDelete(id) {
    if (window.confirm("Видалити цей пункт?")) {
      setServices(s => ({ ...s, [tab]: s[tab].filter(i => i.id !== id) }));
      setEditing(null);
    }
  }

  function resetTab() {
    const label = customTemplates[tab] ? "власного шаблону" : "стандартного шаблону";
    if (window.confirm(`Скинути до ${label}?`)) {
      setServices(s => ({ ...s, [tab]: getTemplateItems(tab) }));
    }
  }

  return (
    <>
    <div className="no-print" style={{ background:th.pageBg, minHeight:"100vh", fontFamily:"Arial, sans-serif", display:"flex", flexDirection:"column" }}>
      {/* Header */}
      <div style={{ background:th.headerBg, borderBottom:`3px solid ${th.headerBorder}`, padding:"14px 20px 0", flexShrink:0, boxShadow:th.headerShadow }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, gap:8 }}>
          <div style={{ fontSize:isMobile?15:18, fontWeight:900, color:th.titleColor, letterSpacing:isMobile?1:2, flexShrink:0 }}>ХІД СЛУЖІННЯ</div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <input ref={importRef} type="file" accept=".json" style={{ display:"none" }} onChange={handleImport}/>

            {/* Кнопка режиму — завжди видима */}
            <button onClick={()=>setViewMode(v=>!v)} style={{ background: viewMode ? th.viewBtnActiveBg : th.viewBtnBg, color: viewMode ? th.viewBtnActiveText : th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 12px", fontWeight:800, fontSize:13, cursor:"pointer" }}>
              {viewMode ? "✏️ Ред." : "📋 Кафедра"}
            </button>

            {/* Десктоп: всі кнопки одразу */}
            {!isMobile && <>
              <button onClick={()=>window.print()} title="Друк / PDF" style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>🖨️</button>
              <button onClick={handleExport} title="Експорт JSON" style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>📤</button>
              <button onClick={()=>importRef.current.click()} title="Імпорт JSON" style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>📥</button>
              <button onClick={()=>setShowArchive(true)} title="Архів служінь" style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>🗄️</button>
              {canArchive() && (
                <button onClick={handleArchive} title="Архівувати поточне служіння" style={{ background:th.saveBg, color:th.saveText, border:"none", borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>📦</button>
              )}
              <button onClick={toggleTheme} style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>
                {themeName === "light" ? "🌙" : "☀️"}
              </button>
              <button onClick={()=>setShowHelp(true)} title="Довідка" style={{ background:th.viewBtnBg, color:th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 11px", fontWeight:800, fontSize:15, cursor:"pointer" }}>❓</button>
            </>}

            {/* Мобільний: кнопка «⋮» + дропдаун */}
            {isMobile && (
              <div ref={menuRef} style={{ position:"relative" }}>
                <button onClick={()=>setMenuOpen(v=>!v)} style={{ background: menuOpen ? th.viewBtnActiveBg : th.viewBtnBg, color: menuOpen ? th.viewBtnActiveText : th.viewBtnText, border:th.viewBtnBorder, borderRadius:8, padding:"7px 13px", fontWeight:900, fontSize:18, cursor:"pointer", lineHeight:1 }}>⋮</button>
                {menuOpen && (
                  <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:300, background:th.modalBg, border:`1px solid ${th.modalBorder}`, borderRadius:12, boxShadow:"0 6px 24px rgba(0,0,0,0.18)", minWidth:190, overflow:"hidden" }}>
                    {[
                      { icon:"🖨️", label:"Друк / PDF",      action: ()=>{ window.print(); setMenuOpen(false); } },
                      { icon:"📤", label:"Експорт JSON",     action: ()=>{ handleExport(); setMenuOpen(false); } },
                      { icon:"📥", label:"Імпорт JSON",      action: ()=>{ importRef.current.click(); setMenuOpen(false); } },
                      { icon:"🗄️", label:"Архів служінь",   action: ()=>{ setShowArchive(true); setMenuOpen(false); } },
                      ...(canArchive() ? [{ icon:"📦", label:"Архівувати служіння", action: ()=>{ handleArchive(); setMenuOpen(false); } }] : []),
                      { icon: themeName==="light" ? "🌙" : "☀️", label: themeName==="light" ? "Темна тема" : "Світла тема", action: ()=>{ toggleTheme(); setMenuOpen(false); } },
                      { icon:"❓", label:"Довідка", action: ()=>{ setShowHelp(true); setMenuOpen(false); } },
                    ].map(({ icon, label, action }) => (
                      <div key={label} onMouseDown={action} style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 18px", cursor:"pointer", color:th.inpText, fontSize:15, borderBottom:`1px solid ${th.inpBorder}` }}
                        onMouseEnter={e=>e.currentTarget.style.background=th.metaBarBg}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{ fontSize:18 }}>{icon}</span>{label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"flex", gap:4, overflowX:"auto", scrollBehavior:"smooth" }}>
          {Object.entries(TEMPLATES).map(([k,v]) => {
            const m = meta[k] || {};
            const sub = [formatDateUkr(m.date), m.leader, m.preacher].filter(Boolean).join(' · ');
            return (
              <button key={k} ref={el=>tabRefs.current[k]=el} onClick={()=>{
                setTab(k);
                setTimeout(()=>{ tabRefs.current[k]?.scrollIntoView({ behavior:"smooth", inline:"start", block:"nearest" }); }, 0);
              }} style={{
                padding: sub ? "7px 14px 8px" : "10px 18px", border:"none", borderRadius:"8px 8px 0 0", cursor:"pointer",
                fontWeight:900, fontSize:12, letterSpacing:1, flexShrink:0,
                display:"flex", flexDirection:"column", alignItems:"center",
                background: tab===k ? v.color : th.tabUnselectedBg,
                color: tab===k ? v.accent : th.tabUnselectedText,
                borderBottom: tab===k ? `2px solid ${v.color}` : "none"
              }}>
                <span style={{ whiteSpace:"nowrap" }}>{v.label}</span>
                {sub && <span style={{ fontSize:9, fontWeight:400, letterSpacing:0, whiteSpace:"nowrap", opacity:0.9, marginTop:3 }}>{sub}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <main style={{ flex:1, overflowY:"auto", padding: isMobile ? "10px 12px" : viewMode ? "12px 16px" : "16px 20px" }}>
        {!viewMode && (
          <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:10, alignItems:"center" }}>
            {customTemplates[tab] && (
              <button onClick={handleDeleteCustomTemplate} title="Видалити власний шаблон" style={{ background:"transparent", border:`1px solid ${th.deleteBtnBorder}`, color:th.deleteBtnText, borderRadius:8, padding:"6px 10px", fontSize:11, cursor:"pointer" }}>✕ власний</button>
            )}
            <button onClick={handleSaveTemplate} title="Зберегти поточні пункти як шаблон" style={{ background:"transparent", border:`1px solid ${th.resetBorder}`, color:th.resetColor, borderRadius:8, padding:"6px 14px", fontSize:12, cursor:"pointer" }}>💾 Зберегти шаблон</button>
            <button onClick={resetTab} title={customTemplates[tab] ? "Скинути до власного шаблону" : "Скинути до стандартного шаблону"} style={{ background:"transparent", border:`1px solid ${th.resetBorder}`, color:th.resetColor, borderRadius:8, padding:"6px 14px", fontSize:12, cursor:"pointer" }}>↺ Скинути{customTemplates[tab] ? " (власний)" : ""}</button>
          </div>
        )}
        <MetaBar meta={meta[tab]} onChange={v=>setMeta(m=>({...m,[tab]:v}))} viewMode={viewMode} th={th} leaderHistory={leaderHistory} onLeaderSave={handleLeaderSave} onLeaderClear={handleLeaderClear} preacherHistory={preacherHistory} onPreacherSave={handlePreacherSave} onPreacherClear={handlePreacherClear}/>
        {items.map((item, i) => (
          <ServiceItem key={item.id} item={item} idx={i+1}
            onEdit={setEditing}
            onDelete={handleDelete}
            onMoveUp={()=>handleMove(item.id, -1)}
            onMoveDown={()=>handleMove(item.id, 1)}
            isFirst={i===0}
            isLast={i===items.length-1}
            viewMode={viewMode} th={th} isMobile={isMobile}
            onShowLyrics={setLyricsItem}/>
        ))}
        {!viewMode && (
          <button onClick={()=>setAdding(true)} style={{ width:"100%", marginTop:4, padding:"14px", background:th.addBtnBg, border:th.addBtnBorder, borderRadius:12, color:th.addBtnText, fontSize:15, fontWeight:700, cursor:"pointer", letterSpacing:0.5 }}>
            + Додати пункт
          </button>
        )}
      </main>

      {editing && <EditModal item={editing} onSave={handleSave} onClose={()=>setEditing(null)} th={th} psalmHistory={psalmHistory}/>}
      {adding  && <AddModal onSave={handleAdd} onClose={()=>setAdding(false)} th={th} psalmHistory={psalmHistory}/>}
      {lyricsItem && <LyricsModal item={lyricsItem} onClose={()=>setLyricsItem(null)} th={th}/>}
      {showArchive && <ArchiveModal archive={archiveHistory} onDelete={handleDeleteArchiveEntry} onClose={()=>setShowArchive(false)} th={th}/>}
      {showHelp && <HelpModal onClose={()=>setShowHelp(false)} th={th}/>}
      <div className="no-print" style={{ padding:"8px 20px", textAlign:"center", fontSize:11, color:th.tabUnselectedText, borderTop:`1px solid ${th.headerBorder}`, flexShrink:0 }}>
        © Andrii Stadnik — всі права захищені
      </div>
    </div>
    <PrintView tab={tab} items={items} meta={meta[tab]}/>
    </>
  );
}
